// ShaderAssembler — ResolvedRecipe[] → GLSL-ES-300 fragment shader source + UBO layout.
// Consumes ONLY ResolvedRecipe (inheritance resolved upstream in RecipeResolver). Program cache
// keyed by the structural hash of the active material set (D1: params-only variants share a program).
import {
	MATERIAL_SAMPLE_GLSL,
	RAMP_MAX_STOPS,
	isModRef, isParamRef,
	type LayerArg, type ParamKind, type ParamRef, type ParamScalar,
} from "../@MaterialTypes.js";
import {
	PRIMITIVE_PRELUDE, floatLit, getPrimitiveDef, literalArgExpr,
} from "../PrimitiveRegistry.js";
import {
	computeParamLayouts,
	type PackedParamLayout,
} from "./UniformPacker.js";
import {
	LIGHTING_GLSL, LIGHTING_UNIFORM_DECLS,
} from "./LightingStage.js";
import {AMBIENT_DETAIL_UNIFORM_DECLS} from "./AmbientDetailStage.js";
import type {ResolvedParam, ResolvedRecipe} from "../RecipeResolverCore.js";
import {recipeSetHash} from "../RecipeResolverCore.js";
import {REGION_FEATHER_BLOCK_COLUMNS, SPLAT_MAX_MATERIALS} from "../Splat/SplatTypes.js";

export interface AssembleResult {
	glsl: string;
	paramLayouts: PackedParamLayout[];
	/** Structural hash of the active material SET (cache key). */
	structuralHash: string;
}

export class MaterialAssembleError extends Error {
	constructor(message: string) { super(message); this.name = "MaterialAssembleError"; }
}

export function globalParamKey(recipeID: string, recipeIndex: number, paramKey: string): string {
	return `${recipeID}/${recipeIndex}/${paramKey}`;
}

/** Set7 — a used param's slot in the VARIANT params texture (texel row-major RGBA; a color's
 *  texel channel is 0, a scalar is its channel, a ramp starts at a texel). */
export type VariantParamSlot = {texel: number; channel: number; kind: ParamKind};


/** Render a layer arg to a GLSL expression in the context of its recipe: literals are baked
 *  (via the schema type), ParamRefs become UBO uniform names.  Color params are declared as
 *  vec4 in the UBO (dodges std140 vec3 alignment) so a ref emits `.rgb`. */
/** Texture-fetch expression for a regionModulated param.  Scalar → 1 texel `.r`; color → 3
 *  consecutive texels → `.rgb`; ramp → NOT supported via the region texture (a vec4[6] needs 6
 *  fetches — Set7's VARIANT texture is the ramp-capable texture; keep this throw per the plan). */
function regionParamExpr(type: ParamKind, column: number): string {
	if (type == "ramp") throw new MaterialAssembleError("regionModulated ramp params are not supported (texture fetch is scalar/color only)");
	if (type == "color") return `texelFetch(u_regionParams, ivec2(${column}, g_regionIndex), 0).rgb`;
	return `texelFetch(u_regionParams, ivec2(${column}, g_regionIndex), 0).r`;
}

/** Set7 — texture-fetch expression for a param in the VARIANT params texture (RGBA32F, one ROW
 *  per identity; column = u_variantRow).  Scalar → `.r` of the texel at `channel` alignment;
 *  color → `.rgb` of the texel (colors are TEXEL-ALIGNED — `variantT`'s `channel` is 0 for them,
 *  so one texelFetch returns the whole colour); ramp → a `variantRamp(<texel>)` call (6 fetches,
 *  §2.4.1).  The texel/channel come from the assembler's cursor map (≡ VariantParams layout). */
function variantParamExpr(type: ParamKind, texel: number, channel: number): string {
	if (type == "ramp") return `variantRamp(${texel})`;
	if (type == "color") return `texelFetch(u_variantParams, ivec2(${texel}, u_variantRow), 0).rgb`;
	// scalar at an arbitrary channel within the row's RGBA texel — fetch the texel and pick the channel.
	return `texelFetch(u_variantParams, ivec2(${texel}, u_variantRow), 0)[${channel}]`;
}

function argExprFor(
	arg: LayerArg, type: ParamKind | "int",
	paramKeyToIndex: Map<string, number>,
	useRegionParams: boolean,
	regionParamColumns: Map<string, number>,
	useVariantParams: boolean,
	variantParamColumns: Map<number, VariantParamSlot>,
): string {
	// Option D — a MODULATED arg: the base value (literal or ParamRef, resolved recursively so it still uses the normal
	// UBO/region machinery) combined with the recipe's macro modulator `g_macroMod`.  `mul` (LENGTHS/SCALES/DENSITIES):
	// base*(1+m*amount); `add` (THRESHOLDS/[0,1]): base+m*amount.  ints/ramps rejected (structural; not a scalar expr).
	if (isModRef(arg)) {
		if (type == "int") throw new MaterialAssembleError("int-typed arg cannot be macro-modulated — ints are structural (baked literals)");
		if (type == "ramp") throw new MaterialAssembleError("ramp-typed arg cannot be macro-modulated (modulate the layer's scalar args instead)");
		const base = argExprFor(arg.base, type, paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns);
		// `amount` may be a ParamRef → the modulation STRENGTH is a live slider (0 = off).
		const amt = argExprFor(arg.amount, "scalar", paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns);
		if ((arg.op ?? "mul") == "add") return `(${base} + g_macroMod * ${amt})`;
		return `(${base} * (1.0 + g_macroMod * ${amt}))`;
	}
	if (isParamRef(arg)) {
		if (type == "int") throw new MaterialAssembleError(`int-typed arg cannot be a ParamRef (${arg.ref}) — ints are structural`);
		const idx = paramKeyToIndex.get(arg.ref);
		if (idx == null) throw new MaterialAssembleError(`ParamRef "${arg.ref}" does not name a declared param of the recipe`);
		// Phase 7 — a regionModulated param reads the per-region params texture at the region's
		// row (g_regionIndex, set in main from the splat's region payload) instead of the UBO.
		const column = useRegionParams ? regionParamColumns.get(arg.ref) : undefined;
		if (column != null) return regionParamExpr(type, column);
		// Set7 — a non-regionModulated USED param reads the VARIANT params texture (per-identity
		// row) instead of the UBO.  Only when the variant path is compiled; otherwise the UBO.
		const variantSlot = useVariantParams ? variantParamColumns.get(idx) : undefined;
		if (variantSlot != null) return variantParamExpr(type, variantSlot.texel, variantSlot.channel);
		return type == "color" ? `u_p_${idx}.rgb` : `u_p_${idx}`;
	}
	return literalArgExpr(arg, type);
}

/** Build the full arg expression map + the set of explicitly-specified arg keys for one layer. */
function layerArgExprs(
	recipe: ResolvedRecipe,
	layer: ResolvedRecipe["layers"][number],
	paramKeyToIndex: Map<string, number>,
	useRegionParams: boolean,
	regionParamColumns: Map<string, number>,
	useVariantParams: boolean,
	variantParamColumns: Map<number, VariantParamSlot>,
): {args: Record<string, string>; specified: Set<string>} {
	const def = getPrimitiveDef(layer.primitive);
	const args: Record<string, string> = {};
	const specified = new Set<string>();
	for (const [argKey, schema] of Object.entries(def.argSchema)) {
		const raw: LayerArg = argKey in layer.args ? layer.args[argKey] : schema.default;
		args[argKey] = argExprFor(raw, schema.type, paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns);
		if (argKey in layer.args) specified.add(argKey);
	}
	return {args, specified};
}

/** Collect the params actually referenced by the recipe set (declaration order; deduped by
 *  global key `${recipeID}/${paramKey}` — param keys are scoped per recipe, so two recipes
 *  extending the same base (Phase 7 user variants) do NOT collide on uniform indices). */
/** Collect the macro-mod FBM param refs for a recipe (Option D).  Returns the raw param keys
 *  referenced by any MacroModConfig field — so the UI can tune octaves/persistence/etc. live. */
function macroModParamKeys(mm: ResolvedRecipe["macroMod"]): string[] {
	if (mm == null) return [];
	const keys: string[] = [];
	for (const v of [mm.wavelength, mm.octaves, mm.lacunarity, mm.persistence, mm.contrast, mm.seedOffset]) {
		if (isParamRef(v)) keys.push(v.ref);
	}
	return keys;
}

export function collectUsedParams(recipes: ResolvedRecipe[]): ResolvedParam[] {
	const used = new Map<string, ResolvedParam>();
	for (const [ri, r] of recipes.entries()) {
		for (const p of r.params) {
			// A param is "used" iff some layer arg (or blendArg) refs it, or a macroMod field refs it.
			let referenced = macroModParamKeys(r.macroMod).includes(p.key);
			for (const l of r.layers) {
				for (const v of Object.values(l.args)) if (argRefsParam(v, p.key)) referenced = true;
				for (const v of Object.values(l.blendArgs)) if (argRefsParam(v, p.key)) referenced = true;
			}
			if (referenced) {
				const paramKey = globalParamKey(r.id, ri, p.key);
				used.set(paramKey, {...p, key: paramKey});
			}
		}
	}
	return [...used.values()];
}

/** Does this layer arg reference the given param key?  Sees THROUGH a ModRef (Option D) to its base + amount — a param referenced only via a modulated arg is still used and must keep its UBO slot (else its uniform compresses out and every later index shifts). */
function argRefsParam(v: LayerArg, key: string): boolean {
	// Both halves of a ModRef can reference params (the base value AND the modulation amount).
	if (isModRef(v)) return argRefsParam(v.base, key) || argRefsParam(v.amount, key);
	return isParamRef(v) && v.ref == key;
}

// ── Per-SLOT param-key → set-wide uniform-index maps ──────────────────────────────────
// Set9 P3 — keyed by SLOT, not recipe id: an F-split run puts the SAME recipe on two slots, and
// each slot owns its own param block (per `globalParamKey`) so a chunk SHARING this program with
// different recipes at those positions has somewhere to put each one's values.
type ParamLookup = {bySlot: Map<number, Map<string, number>>};

function buildParamLookups(recipes: ResolvedRecipe[], usedParamKeys: string[], usedParamGlobalKeys: Map<string, string>): ParamLookup {
	const bySlot = new Map<number, Map<string, number>>();
	for (const [ri, r] of recipes.entries()) {
		const local = new Map<string, number>();
		for (const p of r.params) {
			const gk = globalParamKey(r.id, ri, p.key);
			const idx = usedParamKeys.indexOf(gk);
			if (idx >= 0) local.set(p.key, idx);
		}
		// Id-keying here made a duplicated recipe's 2nd instance OVERWRITE the 1st, so both
		// `eval_i`s read the LAST block — wrong for any chunk sharing the program (Set9 P3).
		bySlot.set(ri, local);
	}
	void usedParamGlobalKeys;
	return {bySlot};
}

/** Resolve the param lookup for the recipe at SLOT `recipeIndex` — slot-keyed, so an F-split run's
 *  two instances of one recipe each resolve to their OWN block (see {@link buildParamLookups}). */
function lookupFor(lookup: ParamLookup, recipeIndex: number): Map<string, number> {
	const m = lookup.bySlot.get(recipeIndex);
	if (m == null) throw new MaterialAssembleError(`no param lookup for recipe slot ${recipeIndex}`);
	return m;
}

/** Per-layer wrapper: `vec4 L_<recipeIdx>_<layerIdx>(vec2 p)` returning (color, height). */
function emitLayerFn(recipeIdx: number, layerIdx: number, recipe: ResolvedRecipe, layer: ResolvedRecipe["layers"][number], paramKeyToIndex: Map<string, number>, useRegionParams: boolean, regionParamColumns: Map<string, number>, useVariantParams: boolean, variantParamColumns: Map<number, VariantParamSlot>): string {
	const def = getPrimitiveDef(layer.primitive);
	if (!def.implemented) throw new MaterialAssembleError(`recipe "${recipe.id}" uses unimplemented primitive "${layer.primitive}"`);
	if (!def.layerUsable) throw new MaterialAssembleError(`recipe "${recipe.id}" uses primitive "${layer.primitive}" which is not layer-usable`);
	const {args, specified} = layerArgExprs(recipe, layer, paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns);
	// The RAW arg values ride along for layer bodies that need to distinguish a literal ramp
	// (JS-bake its mean as a literal — the deterministic, GPU-parity path) from a ParamRef
	// ramp (uniform-backed — compute the mean in-shader from the stops so slider changes
	// track, and the D10 free-variant property is preserved).  Set3 §4.2.
	const raw: Record<string, LayerArg> = {};
	for (const [argKey, schema] of Object.entries(def.argSchema)) {
		raw[argKey] = argKey in layer.args ? layer.args[argKey] : schema.default;
	}
	const body = def.layerBody(args, specified, raw);
	return `
vec4 L_${recipeIdx}_${layerIdx}(vec2 p) {
${body}
}`;
}

/** Layer wrapper reference used by hChain/eval. */
function layerFnName(recipeIdx: number, layerIdx: number): string {
	return `L_${recipeIdx}_${layerIdx}`;
}

/** Derivative-wrapper function name: `D_<recipeIdx>_<layerIdx>` returning (dhdx, dhdz, h). */
function derivFnName(recipeIdx: number, layerIdx: number): string {
	return `D_${recipeIdx}_${layerIdx}`;
}

/** Height-accumulation statement for one layer (blend × contribution semantics). */
function heightAccumStmt(recipeIdx: number, layerIdx: number, layer: ResolvedRecipe["layers"][number]): string {
	const fn = `${layerFnName(recipeIdx, layerIdx)}(p)`;
	if (layer.contributes == "color") return "";
	if (layer.blend == "heightBlend") return `  height = max(height, ${fn}.a);`;
	// maskedBy: the layer's .a IS a mask (not a height) — it SCALES the relief accumulated below
	// (snow thins off a slope: its ripples flatten as coverage goes to 0) and contributes none of
	// its own.  A mask layer should still normally declare contributes:"color" (see the registry
	// note); this branch makes the "both" case behave sanely rather than injecting a 0..1 "height".
	if (layer.blend == "maskedBy") return `  height = height * clamp(${fn}.a, 0.0, 1.0);`;
	// multiply/add/lightModulatedRamp are COLOUR operators; their height contribution is additive
	// like `over` (a detail layer's relief adds to the stack).
	return `  height = height + ${fn}.a;`;
}

/** Gradient-accumulation statement for one layer — the Set11 Phase-1 differentiated form of
 *  {@link heightAccumStmt} (the chain rules mirror the height rules exactly: sum, hard-select
 *  max, product rule for the masked scale).  Emitted into `dChain`. */
function gradientAccumStmt(recipeIdx: number, layerIdx: number, layer: ResolvedRecipe["layers"][number]): string {
	const fn = derivFnName(recipeIdx, layerIdx);
	if (layer.contributes == "color") return "";
	if (layer.blend == "heightBlend") return `  { vec3 d = ${fn}(p); if (d.z > h) { g = d.xy; h = d.z; } }`;
	if (layer.blend == "maskedBy") return `  { vec3 d = ${fn}(p); float m = clamp(d.z, 0.0, 1.0); vec2 dm = (d.z > 0.0 && d.z < 1.0) ? d.xy : vec2(0.0); g = g * m + h * dm; h = h * m; }`;
	return `  { vec3 d = ${fn}(p); g += d.xy; h += d.z; }`;
}

/** Color-accumulation statement for one layer.
 *  `heightBlend` compares the layer's OWN height against `hBelow` — the height accumulated by the
 *  layers BELOW it in the stack.  It must NOT compare against the whole-stack total `hc`: that
 *  total already includes this layer's own height (which cancels) plus every LATER layer's height,
 *  so `L.a - hc` degenerates to `-(sum of later heights)` — a weight driven entirely by unrelated
 *  later layers instead of by "is this layer on top here?".  (That was the dirt-recipe mess: the
 *  pebble weight became pure fine-fbm detail noise, spraying pebble colour uncorrelated with the
 *  actual pebble discs.) */
function colorAccumStmt(recipeIdx: number, layerIdx: number, recipe: ResolvedRecipe, layer: ResolvedRecipe["layers"][number], paramKeyToIndex: Map<string, number>, useRegionParams: boolean, regionParamColumns: Map<string, number>, useVariantParams: boolean, variantParamColumns: Map<number, VariantParamSlot>): string {
	const fn = layerFnName(recipeIdx, layerIdx);
	if (layer.contributes == "height") return "";
	// Generic blend-OPACITY (2026-08-13): `blendArgs.blendFactor` (0..1, default 1) scales how
	// strongly the layer's colour blend applies — `mix(current, <result of the blend>, blendFactor)`.
	// Orthogonal to the blend MODE: it is a multiplicative "amount" knob that works for EVERY mode
	// (over/multiply/add/maskedBy/heightBlend/lightModulatedRamp), so a future recipe can ask "how
	// much mulch tint" / "how strong this detail" without a new mode.  It is an exact no-op when
	// absent (default 1 → the wrapped form collapses to the raw blend), preserving the D1
	// program-cache invariance + byte-parity tests.  Each mode's base statement is computed into a
	// temp (`blended`), then mixed by the factor.
	const factorRaw = layer.blendArgs.blendFactor ?? 1;
	const useFactor = !(typeof factorRaw == "number" && factorRaw == 1);
	const factorExpr = argExprFor(factorRaw as LayerArg, "scalar", paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns);
	const factorSuffix = useFactor
		? `
  color = mix(color, blended, clamp(${factorExpr}, 0.0, 1.0));`
		: "";

	if (layer.blend == "heightBlend") {
		const softness = layer.blendArgs.softness ?? 0.02;
		const softExpr = argExprFor(softness as LayerArg, "scalar", paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns);
		// Byte-parity: the default (no factor) emits EXACTLY the pre-2026-08-13 statement.
		if (!useFactor) return `
  {
    vec4 L = ${fn}(p);
    float w = smoothstep(0.0, max(${softExpr}, 1e-5), L.a - hBelow);
    color = mix(color, L.rgb, w);
  }`;
		return `
  {
    vec4 L = ${fn}(p);
    float w = smoothstep(0.0, max(${softExpr}, 1e-5), L.a - hBelow);
    vec3 blended = mix(color, L.rgb, w);
    color = mix(color, blended, clamp(${factorExpr}, 0.0, 1.0));
  }`;
	}
	if (layer.blend == "multiply") {
		if (!useFactor) return `  color = color * ${fn}(p).rgb;`;
		return `
  {
    vec3 blended = color * ${fn}(p).rgb;
    color = mix(color, blended, clamp(${factorExpr}, 0.0, 1.0));
  }`;
	}
	if (layer.blend == "add") {
		if (!useFactor) return `  color = color + ${fn}(p).rgb;`;
		return `
  {
    vec3 blended = color + ${fn}(p).rgb;
    color = mix(color, blended, clamp(${factorExpr}, 0.0, 1.0));
  }`;
	}
	if (layer.blend == "maskedBy") {
		// The layer's .a is a COVERAGE MASK: 1 = keep what's below, 0 = replace it with the
		// layer's own .rgb (its "uncovered" colour).  This is the snow/dirt coverage operator —
		// note the direction: a mask layer sits ABOVE the material it gates and reveals the
		// uncovered substrate where the mask goes to 0.
		if (!useFactor) return `
  {
    vec4 L = ${fn}(p);
    color = mix(L.rgb, color, clamp(L.a, 0.0, 1.0));
  }`;
		return `
  {
    vec4 L = ${fn}(p);
    vec3 blended = mix(L.rgb, color, clamp(L.a, 0.0, 1.0));
    color = mix(color, blended, clamp(${factorExpr}, 0.0, 1.0));
  }`;
	}
	if (layer.blend == "lightModulatedRamp") {
		// Snow's shadow blue-shift (plan §4.4): remap the layer's own colour toward `shadowColor`
		// in proportion to how UNLIT the fragment is.  Uses the geo+detail-free geometric normal
		// (matGeoNormal) so the shift tracks the terrain's large-scale shading, not the material's
		// micro-relief — a per-grain blue speckle would read as noise, not as shadowed snow.
		const shadow = layer.blendArgs.shadowColor ?? [0.62, 0.70, 0.86];
		const shadowExpr = argExprFor(shadow as LayerArg, "color", paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns);
		const amount = layer.blendArgs.amount ?? 0.6;
		const amountExpr = argExprFor(amount as LayerArg, "scalar", paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns);
		if (!useFactor) return `
  {
    vec4 L = ${fn}(p);
    float lit = clamp(dot(matGeoNormal(), matLightDir()), 0.0, 1.0);
    color = mix(L.rgb, ${shadowExpr}, (1.0 - lit) * clamp(${amountExpr}, 0.0, 1.0));
  }`;
		return `
  {
    vec4 L = ${fn}(p);
    float lit = clamp(dot(matGeoNormal(), matLightDir()), 0.0, 1.0);
    vec3 blended = mix(L.rgb, ${shadowExpr}, (1.0 - lit) * clamp(${amountExpr}, 0.0, 1.0));
    color = mix(color, blended, clamp(${factorExpr}, 0.0, 1.0));
  }`;
	}
	// over = replace.
	if (!useFactor) return `  color = ${fn}(p).rgb;`;
	return `
  {
    vec3 blended = ${fn}(p).rgb;
    color = mix(color, blended, clamp(${factorExpr}, 0.0, 1.0));
  }`;
}

/** Running "height below" update for one layer, used by later heightBlend layers' colour weight.
 *  Mirrors heightAccumStmt's blend semantics (heightBlend = max, over/additive = sum). */
function heightBelowAccumStmt(recipeIdx: number, layerIdx: number, layer: ResolvedRecipe["layers"][number]): string {
	if (layer.contributes == "color") return "";
	const fn = `${layerFnName(recipeIdx, layerIdx)}(p)`;
	if (layer.blend == "heightBlend") return `  hBelow = max(hBelow, ${fn}.a);`;
	if (layer.blend == "maskedBy") return `  hBelow = hBelow * clamp(${fn}.a, 0.0, 1.0);`;
	return `  hBelow = hBelow + ${fn}.a;`;
}

/** The height-chain function: re-evaluates the layer stack at p, accumulating height only. */
function emitHChain(recipeIdx: number, recipe: ResolvedRecipe): string {
	const stmts = recipe.layers.map((l, li) => heightAccumStmt(recipeIdx, li, l)).filter(s => s != "").join("\n");
	return `
float hChain_${recipeIdx}(vec2 p) {
  float height = 0.0;
${stmts}
  return height;
}`;
}

/** The per-layer derivative wrapper `D_<r>_<l>(vec2 p)` returning (dhdx, dhdz, h) — Set11
 *  Phase 1.  Emits the primitive's `derivBody` when present (the analytic gradient), otherwise
 *  SYNTHESISES the layer-local finite difference (correct, just band-limited for that one
 *  layer — the fallback that keeps an unmigrated primitive's stack contribution exact).
 *  The fallback's per-layer forward differences sum to the forward difference of the sum, so an
 *  all-fallback `over` stack reproduces today's normal exactly (the byte-parity test). */
function emitLayerDerivFn(recipeIdx: number, layerIdx: number, recipe: ResolvedRecipe, layer: ResolvedRecipe["layers"][number], paramKeyToIndex: Map<string, number>, useRegionParams: boolean, regionParamColumns: Map<string, number>, useVariantParams: boolean, variantParamColumns: Map<number, VariantParamSlot>): string {
	const def = getPrimitiveDef(layer.primitive);
	const {args, specified} = layerArgExprs(recipe, layer, paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns);
	const raw: Record<string, LayerArg> = {};
	for (const [argKey, schema] of Object.entries(def.argSchema)) {
		raw[argKey] = argKey in layer.args ? layer.args[argKey] : schema.default;
	}
	if (def.derivBody != null) {
		const body = def.derivBody(args, specified, raw, layerFnName(recipeIdx, layerIdx));
		return `
vec3 ${derivFnName(recipeIdx, layerIdx)}(vec2 p) {
${body}
}`;
	}
	// Synthesised fallback — finite-difference THIS LAYER ONLY (the plan §2.1 form).
	const fn = layerFnName(recipeIdx, layerIdx);
	return `
vec3 ${derivFnName(recipeIdx, layerIdx)}(vec2 p) {
  float px = max(matPixelSize(), 1e-5);
  float h  = ${fn}(p).a;
  float hx = ${fn}(p + vec2(px, 0.0)).a;
  float hz = ${fn}(p + vec2(0.0, px)).a;
  return vec3((h - hx) / px, (h - hz) / px, h);
}`;
}

/** The gradient-chain function `dChain_<r>(vec2 p)` returning (dhdx, dhdz, h) — Set11 Phase 1.
 *  Mirrors hChain's blend semantics, differentiated (the plan §2.2 table). */
function emitDChain(recipeIdx: number, recipe: ResolvedRecipe): string {
	const stmts = recipe.layers.map((l, li) => gradientAccumStmt(recipeIdx, li, l)).filter(s => s != "").join("\n");
	return `
vec3 dChain_${recipeIdx}(vec2 p) {
  vec2 g = vec2(0.0);
  float h = 0.0;
${stmts}
  return vec3(g, h);
}`;
}

/** The per-layer VARIANCE wrapper `V_<r>_<l>(vec2 p)` returning the layer's sub-pixel slope
 *  variance — Set11 Phase 2.  Emits the primitive's `varianceBody` when present; otherwise a
 *  constant 0.0 (the exact no-op that keeps unmigrated recipes byte-identical). */
function emitLayerVarianceFn(recipeIdx: number, layerIdx: number, recipe: ResolvedRecipe, layer: ResolvedRecipe["layers"][number], paramKeyToIndex: Map<string, number>, useRegionParams: boolean, regionParamColumns: Map<string, number>, useVariantParams: boolean, variantParamColumns: Map<number, VariantParamSlot>): string {
	const def = getPrimitiveDef(layer.primitive);
	if (def.varianceBody == null) {
		return `
float V_${recipeIdx}_${layerIdx}(vec2 p) { return 0.0; }`;
	}
	const {args, specified} = layerArgExprs(recipe, layer, paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns);
	const raw: Record<string, LayerArg> = {};
	for (const [argKey, schema] of Object.entries(def.argSchema)) {
		raw[argKey] = argKey in layer.args ? layer.args[argKey] : schema.default;
	}
	const body = def.varianceBody(args, specified, raw);
	return `
float V_${recipeIdx}_${layerIdx}(vec2 p) {
${body}
}`;
}

/** The variance chain `varChain_<r>(vec2 p)` — the summed sub-pixel slope variance across the
 *  recipe's layers (variances of independent fields add; Set11 Phase 2 §3.2).  Only layers that
 *  contribute height participate (a colour-only layer has no relief to variance over). */
function emitVarChain(recipeIdx: number, recipe: ResolvedRecipe): string {
	const stmts = recipe.layers
		.map((l, li) => l.contributes == "color" ? "" : `  v += V_${recipeIdx}_${li}(p);`)
		.filter(s => s != "").join("\n");
	return `
float varChain_${recipeIdx}(vec2 p) {
  float v = 0.0;
${stmts}
  return v;
}`;
}

/** The material eval function: color chain + detail normal.  Set11 Phase 1 — the normal comes from `dChain_<r>` (the analytic gradient chain) instead of three finite-differenced `hChain` calls; the sign is NEGATED (the old code computed `(hc - hx) / px` — the negated forward derivative — so `vec3(-dh.x, -dh.y, 1.0)` matches; an inverted sign flips every bump into a dent, caught by the cobblestone GPU parity test).  Set11 Phase 2 — per-layer roughness: `rough` starts at the recipe literal, and each layer with `roughnessOut` set contributes with the SAME weight its colour blend used; then the Toksvig widening from the sub-pixel slope variance (varChain) applies. */
function emitEval(recipeIdx: number, recipe: ResolvedRecipe, paramKeyToIndex: Map<string, number>, useRegionParams: boolean, regionParamColumns: Map<string, number>, useVariantParams: boolean, variantParamColumns: Map<number, VariantParamSlot>, opts: AssembleOptions = {}): string {
	const useVarChain = opts.roughnessAwareSpecular === true;
	// Interleaved: each layer's colour blends against `hBelow` (the layers below it), THEN adds its
	// own height into hBelow for the layers above.  Bottom-up single pass — a heightBlend layer must
	// never see later layers' heights (see colorAccumStmt).
	const colorStmts = recipe.layers.flatMap((l, li) => [
		colorAccumStmt(recipeIdx, li, recipe, l, paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns),
		heightBelowAccumStmt(recipeIdx, li, l),
	]).filter(s => s != "").join("\n");
	// Set10 — the ambient detail normal, whiteout-blended with the recipe's own detail normal
	// (§4.2).  `ambientDetailNormal` returns the raw slope vector (xy = slope offsets, z = 1) and
	// is gated by u_ambDetailStrength inside (0 = an exact no-op: the blend is the identity).
	// The whiteout convention is the codebase's own (LightingStage.lightingShade): a detail
	// normal must never flatten the base below itself.
	// Option D — the macro modulator, HOISTED as the first statement (before dChain_N, whose layer derivative bodies
	// read g_macroMod too) so the colour, gradient and variance chains see the SAME value.  Absent modulator = no
	// statement; g_macroMod keeps its 0.0 initialiser and every ModRef collapses to its base (byte-identical GLSL).
	// Each MacroModConfig field may be a literal (baked) or a ParamRef (a live UI slider, resolved
	// through the same UBO/region machinery as layer args — so `octaves`, `persistence`, etc. are tunable).
	const mm = recipe.macroMod ?? null;
	const mArg = (v: ParamScalar | ParamRef, type: ParamKind | "int"): string =>
		argExprFor(v, type, paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns);
	const macroModStmt = mm == null ? "" : `  g_macroMod = macroModVal(p, ${mArg(mm.wavelength, "scalar")}, int(${mArg(mm.octaves, "scalar")}), ${mArg(mm.lacunarity, "scalar")}, ${mArg(mm.persistence, "scalar")}, ${mArg(mm.contrast, "scalar")}, ${mArg(mm.seedOffset, "scalar")});\n`;
	return `
MaterialSample eval_${recipeIdx}(vec2 p, vec3 baseAlbedo) {
${macroModStmt}  vec3 dh = dChain_${recipeIdx}(p);
  float hc = dh.z;
  vec3 color = baseAlbedo;
  float hBelow = 0.0;
${colorStmts}
  // Set11 Phase 1 — the analytic gradient, negated to match the old forward-difference sign.
  vec3 n = normalize(vec3(-dh.x, -dh.y, 1.0));
  // Set10 — terrain ambient detail (the sub-lattice band): pixel-scale relief the height
  // lattice cannot represent (§1.1), band-limited to [2px, k*cellSize] and crossfaded so an
  // octave never pops when it crosses the geometry crossover (§4.1/§4.2).  Gated by
  // u_ambDetailStrength: 0 = an exact no-op (also skips the per-fragment noise cost — the
  // Set10 §7 test 9 "byte-identical to pre-Set10" bisection aid).
  if (u_ambDetailStrength > 0.0) {
    vec3 na = ambientDetailNormal(p);
    n = normalize(vec3(n.xy + na.xy, n.z * na.z));
  }
  MaterialSample s;
  s.albedo = color;
  s.normal = n;
${useVarChain ? roughStmts(recipeIdx, recipe, paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns) : `  s.roughness = ${floatLit(recipe.roughness)};`}
  s.height = hc;
  return s;
}`;
}

/** Set11 Phase 2 — the roughness statements inside eval.  `rough` starts at the recipe literal; each layer with `roughnessOut` set contributes with the same weight its colour blend used.  Then the Toksvig widening: rough = sqrt(rough^2 + u_varianceGain * varChain) — clamped.  Only emitted when `roughnessAwareSpecular` is on (off = a bare literal, no varChain; the lighting call is always the 4-arg PBR form — Set14 P9). */
function roughStmts(recipeIdx: number, recipe: ResolvedRecipe, paramKeyToIndex: Map<string, number>, useRegionParams: boolean, regionParamColumns: Map<string, number>, useVariantParams: boolean, variantParamColumns: Map<number, VariantParamSlot>): string {
	const lines: string[] = [`  float rough = ${floatLit(recipe.roughness)};`];
	for (let li = 0; li < recipe.layers.length; li++) {
		const layer = recipe.layers[li];
		if (layer.roughnessOut == null) continue;
		const fn = layerFnName(recipeIdx, li);
		const expr = argExprFor(layer.roughnessOut, "scalar", paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns);
		if (layer.blend == "heightBlend") {
			const softness = layer.blendArgs.softness ?? 0.02;
			const softExpr = argExprFor(softness as LayerArg, "scalar", paramKeyToIndex, useRegionParams, regionParamColumns, useVariantParams, variantParamColumns);
			lines.push(`  { vec4 L = ${fn}(p); float w = smoothstep(0.0, max(${softExpr}, 1e-5), L.a - hBelow); rough = mix(rough, ${expr}, w); }`);
		} else if (layer.blend == "maskedBy") {
			lines.push(`  { vec4 L = ${fn}(p); rough = mix(${expr}, rough, clamp(L.a, 0.0, 1.0)); }`);
		} else {
			// over/multiply/add/lightModulatedRamp — treat as replace (the plan §3.2(b) table).
			lines.push(`  rough = ${expr};`);
		}
	}
	lines.push(`  float sv = varChain_${recipeIdx}(p);`);
	lines.push(`  rough = clamp(sqrt(rough * rough + u_varianceGain * sv), 0.0, 1.0);`);
	lines.push(`  s.roughness = rough;`);
	return lines.join("\n");
}

/** Emit the MaterialParams UBO declaration from the layouts (declaration order = layout order). */
function emitUBO(layouts: PackedParamLayout[]): string {
	if (layouts.length == 0) return "";
	const members = layouts.map(l => {
		if (l.kind == "scalar") return `  float u_p_${l.index};`;
		if (l.kind == "color") return `  vec4 u_p_${l.index};`;
		return `  vec4 u_p_${l.index}[6];`;
	}).join("\n");
	return `layout(std140) uniform MaterialParams {
${members}
};`;
}

/** Compute the structural hash of the ACTIVE SET: recipes sorted by id, joined (order-independent
 *  within the set; two sets differing only in recipe ORDER cache-hit). Shared with RecipeResolver
 *  (recipeSetHash) so Phase 6's warm-up dedupes by the SAME key the program cache uses. */
export function materialSetHash(recipes: ResolvedRecipe[]): string {
	return recipeSetHash(recipes);
}

export interface AssembleOptions {
	/** When true, emits the Phase-3 lighting stage (u_lightDir/u_ambient/u_sunIntensity/u_slopeScale/
	 *  u_geoNormal uniforms + the hillshade + after-`slope_scale` detail-normal blend), and `main`
	 *  applies it to the material's albedo.  False (Phase-2 default) keeps the raw-albedo main. */
	includeLighting?: boolean;
	/** Phase 5 — when true, emits the splat field: the u_splatIndex/u_splatDist/u_splatDist2 (RGBA8UI/RGBA32F) DataTexture uniforms + the ≤SPLAT_MAX_MATERIALS-way evaluate-and-lerp in `main` (plan §6; recipes.length must be 1..SPLAT_MAX_MATERIALS). */
	useSplat?: boolean;
	/** Phase 5 — the splat lattice size (samples per axis). Must match the rasterised texture (plan §12.1: CHUNK_N today, 65 if boundaries look soft). Defaults to SPLAT_LATTICE_SIZE. */
	splatSize?: number;
	/** Phase 7 — when true, emits the per-region params texture (`u_regionParams` R32F DataTexture,
	 *  one ROW per assignment region; a sample's region index comes from the splat's region payload).
	 *  Params declared `regionModulated` are fetched from this texture (texelFetch at the param's
	 *  column in the region's row) instead of the UBO — plan §6: a region costs ~a dozen floats in
	 *  the DB + one texel fetch.  Only meaningful with `useSplat` (the region index comes from the
	 *  splat).  When off (default), regionModulated params fall back to their UBO value (the
	 *  resolved default) — byte-identical to the Phase 5/6 path. */
	useRegionParams?: boolean;
	/** Phase 7 — column count of the region params texture (params are packed in a fixed column
	 *  layout per region).  Defaults to REGION_PARAMS_COLUMNS; must match the uploader's layout. */
	regionParamsColumns?: number;
	/** Set7 (2026-08-25) — float-column width of the VARIANT params texture's row (one row per
	 *  identity; texels = width/4).  Defaults to VARIANT_PARAMS_COLUMNS; must match the
	 *  manager's uploaded texture width. */
	variantRowColumns?: number;
	/** Set7 (2026-08-25) — when true, emits the per-identity VARIANT params texture
	 *  (`u_variantParams` RGBA32F DataTexture, one ROW per recipe-identity set, indexed per chunk
	 *  by the plain `int` uniform `u_variantRow`) + the `variantRamp()` helper.  Every USED param
	 *  (that isn't `regionModulated`) is fetched from the variant texture instead of the UBO —
	 *  this is the Set6→Set7 replacement for the per-variant param `UniformsGroup` (single shared
	 *  texture → O(1) in variant count, sidestepping the binding-point ceiling).  The UBO path
	 *  stays alive for non-migrated params and as the fallback.  Only meaningful with `useSplat`;
	 *  OFF (default) keeps the UBO path byte-identical.  See `VariantParams.ts`. */
	useVariantParams?: boolean;
	/** Set11 Phase 2 — when true, emits the roughness-aware specular path: the per-layer `roughnessOut` plumbing (rough statements inside eval) and `varChain_N` + `u_varianceGain` (the Toksvig widening).  OFF = a bare `s.roughness` literal (no per-layer roughnessOut, no varChain) — the lighting call itself is ALWAYS the 4-arg PBR form (the 3-arg hardcoded-32 Blinn-Phong path was deleted in Set14 P9), so `s.roughness` must always be populated.  The LIVE path always passes true. */
	roughnessAwareSpecular?: boolean;
	/** 2026-08-13 — emit the terrain-REGION clip: `u_terrainRegionCount`/`u_terrainRegions` +
	 *  a per-fragment alpha zero-out for fragments outside every active TerrainRegion AABB.
	 *  Restores the legacy bake's region clipping for the shader path. Default OFF. */
	terrainRegionClip?: boolean;
	/** Set14 P7 §4.11 — emit the OUTPUT TRANSFER CHAIN at the end of `main`, after `out_Color` is
	 *  written (and after the region clip, so the alpha survives): three's LIVE tone-mapping
	 *  (`#ifdef TONE_MAPPING`) + `linearToOutputTexel` (sRGB encode). three injects both functions
	 *  into the prefix per the RENDERER's current settings (`getToneMappingFunction` /
	 *  `getTexelEncodingFunction`), so calling them directly keeps the setting fully live without
	 *  hard-coding ACES. The `#ifdef TONE_MAPPING` guard means under `NoToneMapping` the tone-map
	 *  step vanishes and (with a LINEAR output color space, where `linearToOutputTexel` is the
	 *  identity) the result is byte-identical to the pre-P7 shader. Default OFF — the Node/GPU
	 *  lanes that compile the fragment standalone (no three prefix) keep the pre-P7 raw output,
	 *  and the live path (`TerrainMaterialManager`) passes true. */
	outputTransfer?: boolean;
	/** Buildings Set1 §7.1 — compile for an ARBITRARY MESH surface (Building walls/floors/roof +
	 *  Door/Window models) instead of a terrain chunk. Implies `useSplat: false`,
	 *  `useRegionParams: false`, `terrainRegionClip: false`, and `recipes.length == 1`. The
	 *  material's 2D sample coordinate comes from a per-vertex `aSurfaceUV` attribute (metres)
	 *  rather than world XY, and the lighting normal comes from the GEOMETRY normal
	 *  (`vSurfaceNormal`) rather than the per-chunk `u_geoNormal` lattice. `geoHeight` resolves to
	 *  the `u_surfaceElevation` uniform (the mesh's world Z origin). Everything else — the param
	 *  UBO packing, layer/blend chain, `MaterialSample` contract, roughness-aware specular, output
	 *  transfer — is shared verbatim with terrain. Strictly additive: the non-surface path emits
	 *  its existing literal strings unchanged (the P6 byte-identical regression). */
	surfaceMode?: boolean;
	/** 2026-08-25 — DEBUG SHIM (vscode integrated-browser perf escape hatch).  When true, the
	 *  assembled fragment is the tiny self-contained `emitDebugShimGLSL` shader (see ShaderAssembler)
	 *  instead of the real per-recipe layer-stack shader: it resolves each recipe-set slot to a
	 *  deterministic hashed colour + per-material strip angle (from the `u_debugShimColors` packed
	 *  values the manager derives per-slot from the recipe-id hash) and paints the splat-assigned
	 *  materials by feathering toward that slot's colour by the splat distance — so different
	 *  regions stay VISUALLY distinguishable WITHOUT the massive compile cost of the real
	 *  Set16/Set17 material shaders on SwiftShader/ANGLE.  The splat textures / geometry uniforms
	 *  are still declared + bound (the renderer plumbing runs unchanged); only the per-fragment
	 *  surface evaluator is replaced.  The structural hash is FIXED (`debugShim`) → every recipe
	 *  set maps to ONE WebGLProgram.  Ignored when `surfaceMode` is true. */
	debugShim?: boolean;
}

/** Phase 7 — default column count of the region params texture (plan §6: one row per region).
 *  The uploader + assembler must agree; a region costs ≤ this many float columns. */
export const REGION_PARAMS_COLUMNS = 64;

/** Set7 — default FLOAT-column width of the VARIANT params texture's row (one row per identity;
 *  texel width = VARIANT_PARAMS_COLUMNS / 4).  This is a FLOOR, not a hard budget: when
 *  `variantRowColumns` is omitted, the width is AUTO-COMPUTED to fit the set's used params (plan
 *  §2.1 "width = max(total columns) rounded up") — the starfield map's ~97 used uniforms need
 *  more than this floor, and the manager's shared texture grows to match.  `variantRowColumns` is
 *  only an EXPLICIT fixed budget when the caller supplies it.  The manager + assembler agree on
 *  the auto-width (same cursor walk). */
export const VARIANT_PARAMS_COLUMNS = 256;

/** Splat lattice size default — mirrors the chunk sample lattice (CHUNK_N). The shader define must always match the rasterised texture. */
export const SPLAT_LATTICE_SIZE = 33;

/** 2026-08-13 — max active TerrainRegion AABBs for the region-clip uniform array (a count >
 *  this would be a GLSL error; maps have a handful of regions). */
export const TERRAIN_REGION_CLIP_MAX = 16;

/** Emit the terrain-REGION clip uniforms + helper (2026-08-13): `main` zeroes `out_Color.a`
 *  outside every active TerrainRegion AABB (the legacy bake's per-pixel alpha test). */
function emitTerrainRegionClipGLSL(): string {
	return `
// Terrain-region clip (2026-08-13): active TerrainRegion AABBs (world space), each = (minX, minY, maxX, maxY).
// u_terrainRegionCount 0 = no clip (the legacy bake's noneVisible case); count > 0 clips fragments
// outside every AABB to alpha 0 (the legacy bake's per-pixel region-visibility alpha test).
uniform int u_terrainRegionCount;
uniform vec4 u_terrainRegions[${TERRAIN_REGION_CLIP_MAX}];

bool terrainRegionClip(vec2 wx) {
	if (u_terrainRegionCount <= 0) return true; // no regions — render fully
	for (int i = 0; i < ${TERRAIN_REGION_CLIP_MAX}; i++) {
		if (i >= u_terrainRegionCount) break;
		vec4 r = u_terrainRegions[i];
		if (wx.x >= r.x && wx.x <= r.z && wx.y >= r.y && wx.y <= r.w) return true; // inside a region
	}
	return false;
}
`;
}

/** Set7 — Emit the VARIANT params texture uniforms + the variantRamp() helper.  The texture is
 *  a single RGBA32F DataTexture (one ROW per recipe-identity set; u_variantRow = the row),
 *  NEAREST sampled via texelFetch (never texture() — no filtering).  A used param's slot is its
 *  (texel, channel) in the row — mirrors VariantParams.buildVariantParamLayouts.  variantRamp
 *  reads 6 consecutive RGBA stop-texels into a vec4[6] (6 fetches per ramp, §2.4.1) — the same
 *  stops-in by-value shape rampVal(float t, vec4 stops[6]) consumes. */
function emitVariantParamsGLSL(rowFloatWidth: number): string {
	const texelWidth = Math.floor(rowFloatWidth / 4);
	return `
// Set7 — the per-identity VARIANT params texture (RGBA32F).  One ROW per recipe-identity set;
// the renderer sets u_variantRow per chunk.  texelFetch (nearest) — no filtering.
uniform sampler2D u_variantParams; // RGBA32F: width ${texelWidth} texels × N rows (N = identity variants)
uniform int      u_variantRow;     // the identity set's row in u_variantParams (per chunk)

// Read the 6 RGBA stop-texels of a ramp starting at texel T into a vec4[6] (the same
// stops-in by-value shape rampVal(float t, vec4 stops[6]) consumes).  6 fetches per call.
vec4[6] variantRamp(int texel) {
	return vec4[6](
		texelFetch(u_variantParams, ivec2(texel + 0, u_variantRow), 0),
		texelFetch(u_variantParams, ivec2(texel + 1, u_variantRow), 0),
		texelFetch(u_variantParams, ivec2(texel + 2, u_variantRow), 0),
		texelFetch(u_variantParams, ivec2(texel + 3, u_variantRow), 0),
		texelFetch(u_variantParams, ivec2(texel + 4, u_variantRow), 0),
		texelFetch(u_variantParams, ivec2(texel + 5, u_variantRow), 0)
	);
}`;
}

/** Emit the splat-field uniforms + splatSample. u_splatIndex MUST be RGBA8UI (integer texture via usampler2D — normalized RGBA8 returns 1/255 → uvec4(0) → no index ever matches). texelFetch at the sample's lattice coords — never texture() (filtering would invent materials at chunk edges). P3 (Set12): the WEIGHT textures are now `u_splatDist` (RGBA32F, LINEAR-filtered — see below); the region payload (RGBA8UI, same lattice — the WINNING region per sample, map index + 1) is sampled the same way; g_regionIndex feeds the region-params texture, which carries the per-region feather/curve/noise/opaque values the shader feathers the distances with. Set7 §9 — the 4→8 raise splits the distance field into TWO RGBA32F textures (u_splatDist slots 0–3 + u_splatDist2 slots 4–7); `splatSample` outputs both vec4s. */
function emitSplatGLSL(useRegionParams: boolean): string {	return `
precision highp usampler2D;   // integer samplers need an explicit precision in ES 300
uniform usampler2D u_splatIndex;  // RGBA8UI: texel .r = the ONE recipe index for this sample (integer texture — see docblock)
uniform sampler2D  u_splatDist;   // RGBA32F: texel components = the ≤4 per-slot SIGNED DISTANCES in METRES (slots 0-3; negative = inside; clamped to ±(feather+noiseAmp) by the rasteriser)
uniform sampler2D  u_splatDist2;  // RGBA32F: slots 4-7 of the ≤8-way splat (Set7 §9; only emitted when the set uses >4 recipes — unused-uniform uploads are harmless)
// P5 — per-slot EFFECTIVE DOMINANCE (the smin soft-union's gate, plan §4.2 C): slot i's
// dominance, set per chunk by the renderer from the rasterised chunk.  The blend chain
// smin-unions the DISTANCE FIELDS of same-dominance slots (equal-dominance materials meet
// smoothly) and keeps the ordered paint-over between distinct dominances.  A PER-CHUNK uniform
// (not baked into the program key): a dominance edit changes the uniform only — no program
// recompile, no splat re-rasterise (the D1 params-only invariance).  Set7 §9 — two vec4s
// (u_slotDominanceA slots 0-3, u_slotDominanceB slots 4-7).
uniform vec4 u_slotDominanceA;
uniform vec4 u_slotDominanceB;
// Set12 followup (2026-08-25) — per-slot RESOLVED FEATHER in metres (the "fade-to-wrong-
// material" fix): slot i's feather = the MAX (feather + edgeNoise.amplitude) among the regions
// sharing that slot (the clamp the rasteriser stored each slot's distance with).  regionWeight
// feathers EACH slot with its OWN feather (not the winning region's single g_regionFeather),
// so a bbox-overlapping-but-far region's distance (clamped to its own +feather) saturates
// to weight 0 and can no longer bleed a phantom partial weight under the fading top region.
// Same PER-CHUNK-uniform lifecycle as u_slotDominance (a feather edit changes the uniform only —
// no program recompile, no splat re-rasterise).  Set7 §9 — two vec4s (slots 0-3 + slots 4-7).
uniform vec4 u_slotFeatherA;
uniform vec4 u_slotFeatherB;
// Set12 followup (2026-08-25) — per-slot SOFT-UNION opt-in (1 = this slot is a soft-union
// participant; 0 = ordered paint-over).  slot i = 1 iff EVERY region sharing that slot has
// softUnion explicitly set (the rasteriser's AND rule).  The chain smin-unions an equal-
// dominance PAIR only when BOTH slots are participants — so a far-but-bbox-overlapping equal-
// dominance region in the adjacent slot can never be accidentally soft-unioned with it (the
// "water replaces base" footgun).  Same PER-CHUNK-uniform lifecycle as u_slotFeather.  Set7
// §9 — two vec4s (slots 0-3 + slots 4-7).
uniform vec4 u_slotSoftUnionA;
uniform vec4 u_slotSoftUnionB;
// Set12 P5 (2026-08-27) — per-slot EDGE-NOISE (amplitude in metres + feature scale in metres):
// slot i's boundary-warp params for the shader's per-fragment fbm displacement (the "edge noise
// gets cut off by the polygon edge" fix).  Previously the noise came from the WINNING region's
// params row (g_regionNoiseAmp/Scale via setRegionIndex) — which reads 0 just OUTSIDE the
// polygon, so the warp's outward teeth were clipped at the region's geometric edge (the inside
// half warped, the outside half was a straight distance).  Making the noise per-slot (like
// u_slotFeather) means a fragment just OUTSIDE the polygon (positive distance, blended toward
// the base) still gets the region's warp — and because fbmVal is continuous in world space, the
// warp is seamless across the polygon boundary.  Slot 0 (the base) stays amp 0 / scale 1 (never
// displaced).  Same PER-CHUNK-uniform lifecycle as u_slotFeather.  Set7 §9 — two vec4s (slots
// 0-3 + slots 4-7).
uniform vec4 u_slotNoiseAmpA;
uniform vec4 u_slotNoiseAmpB;
uniform vec4 u_slotNoiseScaleA;
uniform vec4 u_slotNoiseScaleB;
${useRegionParams ? `
uniform usampler2D u_splatRegion; // RGBA8UI: texel .r = the WINNING region index for this sample (region-params row; 0 = the base)
uniform sampler2D  u_regionParams; // R32F: one ROW per region; columns = the region's feather/curve/noise/opaque block + the recipe's regionModulated params (plan §3.6)` : ""}

// Sample the splat at the sample's lattice coords.  The INDEX texel is fetched with texelFetch
// (nearest — integer, non-interpolatable; texture() would invent materials at chunk edges).
// The DISTANCE texels use texture() with LINEAR filtering — the point of storing distances.  The
// lattice is shared on chunk edges (texel (CHUNK_N-1) sits exactly on the far edge and both
// chunks own it — same world point → same distance), so linear filtering across a chunk boundary
// reads the neighbour's true distance (seam-free by construction; tested in §7).
//
// NOTE (Set9 §3.4, corrected 2026-08-30): the lattice is sampled at TEXEL CENTRES — lat is
// rounded to the integer lattice index BEFORE the sample, so the LINEAR filter is effectively a
// NO-OP (the field is piecewise-constant per texel; bilinear interpolation is only a factor
// between the rounded integer coords, which is always exactly on a texel centre).  The earlier
// "per-fragment interpolation of a smooth near-linear distance field survives bilinear filtering
// almost exactly, so the falloff curve + edge noise apply per fragment at ANY sharpness" claim
// was INACCURATE — the feather curve is evaluated per-fragment, but on a staircase (quantized)
// distance field, so any feather smaller than ~1 texel renders as a texel-quantized hard edge.
// The texel size is a fixed SCREEN-px size the user controls (see renderChunk_maxPxPerSample /
// renderChunk_maxSamplesPerFineCellEdge in MapSettings.ts), NOT a fixed distance in metres.
// True per-fragment bilinear sampling is tracked as future work (Set9 §9), independent of the
// slot-assignment fixes.
// Set7 §9 — two vec4 outputs (d0 = slots 0-3, d1 = slots 4-7); both halves share the same
// lattice coord, so they read the SAME world point.
void splatSample(vec2 wx, out ivec2 lat, out uvec4 idx, out vec4 d0, out vec4 d1) {
	// The chunk's world AABB is (u_origin, u_origin + u_chunkSize); the lattice is CHUNK_N×CHUNK_N.
	// texelFetch takes integer texel coords — the inverse mapping world → texel is exact:
	// col = (wx.x - u_origin.x) / u_chunkSize * (CHUNK_N - 1) (u_chunkSize = the chunk's width).
	// NEAREST-SAMPLE (round, not floor) — see matGeoNormal: texel (CHUNK_N-1) sits EXACTLY on
	// the chunk's far edge; round() makes the boundary fragment map to the same texel the
	// neighbour chunk's near-edge maps to (floor() jumps one cell early → material seams).
	lat = ivec2(round((wx - u_origin) / max(u_chunkSize, 1e-6) * float(CHUNK_N - 1)));
	idx = texelFetch(u_splatIndex, lat, 0);
	// LINEAR-filtered texture() — the whole point of the distance representation (see docblock).
	// A linear-filtered distance field never invents a material: a slot's distance is only
	// consumed where the index says the material is present, and where it is present the two-
	// texel linear sample reads the NEIGHBOUR texel's slot value — which is the SAME world
	// distance (the lattice is shared on chunk edges), so no material can bleed across.
	d0 = texture(u_splatDist, (vec2(lat) + 0.5) / float(CHUNK_N));
	d1 = texture(u_splatDist2, (vec2(lat) + 0.5) / float(CHUNK_N));
}
`;
}

/** Emit the P3 feather machinery (Set12 §3.6): the per-region globals + `regionWeight()` +
 *  `setRegionIndex()`.  Emitted AFTER the primitive prelude — `regionWeight` calls `fbmVal`
 *  (the P5 edge-noise displacement), which is defined there (declare-before-use; the prelude
 *  order matters — matPixelSize precedes it, and this follows it).
 *
 *  P5 — also emits the smin soft-union pair helper `smin2()` (plan §4.2 A→C): the blend chain
 *  soft-unions the DISTANCE FIELDS of equal-dominance slot pairs (the pair's composite
 *  coverage weight comes from the smin'd distance, and the pair's colours blend by relative
 *  weight), while distinct-dominance slots keep the ordered P3 paint-over. */
function emitSplatFeatherGLSL(useRegionParams: boolean): string {
	return useRegionParams ? `
// The region params row for the winning region (set once in main by setRegionIndex): the
// feather band, falloff curve, edge-noise displacement + amplitude, and the opaque flag — the
// P3 per-fragment feather inputs (plan §3.6).  Row 0 (the base) holds defaults; a sample with
// no winning region never calls regionWeight with a non-zero distance (slot distances are
// +huge → weight 0).
int g_regionIndex = 0;
float g_regionFeather = 2.0;
int   g_regionCurve = 0;         // 0 = smoothstep, 1 = linear, 2 = step
float g_regionNoiseAmp = 0.0;
float g_regionNoiseScale = 1.0;
float g_regionOpaque = 0.0;

// The falloff curve inside the feather band (plan §3.6): t in [0,1] (0 = fully outside →
// weight 0, 1 = fully inside → weight 1).  The smoothstep form must stay
// smoothstep(edge0, edge1, x) with edge0 < edge1 — the inverted form is UNDEFINED in
// GLSL ES 300 (has bitten this codebase twice, Set9 Phase 4).
float regionCurveWeight(float t) {
	if (g_regionCurve == 2) return step(0.5, t);              // razor — hard edge mid-band
	if (g_regionCurve == 1) return t;                          // linear
	return t * t * (3.0 - 2.0 * t);                            // smoothstep (spec-clean)
}

// Convert a slot's signed distance to a blend weight (plan §3.6), feathered with the SLOT'S OWN
// resolved feather (metres) passed in from the chain — NOT the winning region's single
// g_regionFeather.  This is the Set12 followup "fade-to-wrong-material" fix: each slot's
// distance was clamped by the rasteriser to its own ±(feather + noiseAmp), so feathering it
// with its own resolved feather (regionWeight(d, slotFeather)) makes a far-but-bbox-overlapping
// region's positive distance saturate to weight 0 exactly.  feather <= 0 = hard edge (step at the
// boundary).  Edge noise (P5) warps the boundary per fragment with fbm — the displacement is
// added to the distance (positive noise moves the boundary inward: a pixel just outside (d
// slightly positive) becomes negative → inside).
// Set12 P5 (2026-08-27) — the noise/scale are now PER-SLOT too (noiseAmp, noiseScale passed in
// from the chain, like feather), NOT the winning region's single g_regionNoiseAmp/Scale.  The
// old global attribution read 0 just OUTSIDE the polygon (u_splatRegion points at the region
// only when the sample is strictly inside), so the warp's outward teeth were clipped at the
// region's geometric edge.  Per-slot noise fixes that, and because fbmVal is evaluated at
// continuous world coords the warp is seamless across the polygon boundary.  noiseAmp <= 0 =
// no displacement on this slot (the base slot always passes 0).  Invariant: the base slot's
// distance is +huge → regionWeight = 0 → the base never fades itself.
//
// PHANTOM — why there is NO d < ... warp gate here: the phantom (a far/absent region's
// saturated distance, clamped to +clampAmt = feather + noiseAmp, rendering weight > 0) is
// prevented by the rasteriser emitting the slot's TRUE feather (slotFeather = feather, not
// feather + noiseAmp).  A saturated sample has dd = clampAmt + fbm*noiseAmp >= feather
// (since fbm >= -1), and regionWeight(dd >= feather, feather) = 0 — for any feather >= 0
// (feather 0 via the hard-edge branch, feather > 0 because smoothstep saturates at dd =
// +feather).  So the noise can never invent material where the field says there is none, with
// NO gate.  A d < noiseAmp gate was TRIED but it REINTRODUCED the feather>0 cutoff: when
// feather > noiseAmp, the gate turned the warp off mid-feather-band at d = noiseAmp, so just
// inside (fbm≈-1 pushed dd≈0 → weight ~0.5) met just outside (warp off, dd=d → weight rising
// back) — a hard non-monotonic jump/ring.  With the gate removed, the warp applies continuously
// across the whole field and the teeth fade smoothly (the correct feathered-noise look).
float regionWeight(vec2 wx, float d, float feather, float noiseAmp, float noiseScale) {
	float dd = d;
	if (noiseAmp > 0.0) {
		// fbmVal mode 0 returns ~[-1, 1] → the displacement is ±noiseAmp metres (wx/noiseScale
		// makes the noise's lattice cell = noiseScale metres).  (The plan's §3.6 sketch wrote
		// (fbm2(...) * 2.0 - 1.0) * noiseAmp for a [0,1]-valued fbm; our fbmVal is already
		// centred — the *2-1 would double-shift it to [-3, 1].)
		dd += fbmVal(wx / noiseScale, 1.0, 3, 2.0, 0.5, 0) * noiseAmp;
	}
	if (feather <= 0.0) return dd < 0.0 ? 1.0 : 0.0;
	// spec-clean: edge0 < edge1 (feather > 0 by the gate above)
	return 1.0 - smoothstep(-feather, feather, dd);
}

// P5 — the smin soft-union pair helper (plan §4.2 A→C).  Same-dominance slot pairs (their
// slotDominance entries match) soft-union their DISTANCE FIELDS: the pair's effective
// coverage weight comes from smin(dA, dB) — the pair's boundary is the softer of the two
// feather curves at each fragment, so two equal-dominance materials meet SMOOTHLY (no hard
// seam — the §4.2 C aesthetic fix), and each member's own interior (weight 1) still renders
// at full strength.  k = the soft-union's smoothness in metres (10.0 = a ~10 m blend width —
// in the same order as a typical feather, so an equal-dominance seam reads as "these two
// materials are one region" rather than a hard vector edge).  A distinct-dominance pair (the
// common case) must NOT smin — the lower member is a DIFFERENT (painted-under) material and
// its distance must only feather its own boundary (splatBlendPair gates on the uniform).
float smin2(float a, float b) {
	const float k = 10.0;
	return min(a, b) - (1.0 / k) * max(k - abs(a - b), 0.0);
}

void setRegionIndex(ivec2 lat) {
	int r = int(texelFetch(u_splatRegion, lat, 0).r);
	g_regionIndex = r;
	g_regionFeather = max(texelFetch(u_regionParams, ivec2(0, r), 0).r, 0.0);
	g_regionCurve = int(round(texelFetch(u_regionParams, ivec2(1, r), 0).r));
	g_regionNoiseAmp = max(texelFetch(u_regionParams, ivec2(2, r), 0).r, 0.0);
	g_regionNoiseScale = max(texelFetch(u_regionParams, ivec2(3, r), 0).r, 1e-3);
	g_regionOpaque = texelFetch(u_regionParams, ivec2(4, r), 0).r;
}
` : `
// useRegionParams OFF — the region globals keep their defaults (the region textures are
// absent; the shader feathers with the resolved defaults — byte-identical to the UBO path).
float g_regionFeather = 2.0;
int   g_regionCurve = 0;
float g_regionNoiseAmp = 0.0;
float g_regionNoiseScale = 1.0;
float g_regionOpaque = 0.0;

// Same feather curve as the region-params path (noise is off — no fbm call needed here, but
// regionWeight must exist for main's blends under useSplat-without-useRegionParams).  The slot's
// own resolved feather + noise are passed in by the chain (see the region-params version's
// docblock); noiseAmp is always 0 on this path, so the warp is skipped entirely.
float regionWeight(vec2 wx, float d, float feather, float noiseAmp, float noiseScale) {
	float dd = d;
	if (feather <= 0.0) return dd < 0.0 ? 1.0 : 0.0;
	return 1.0 - smoothstep(-feather, feather, dd);
}

// Same smin soft-union pair helper as the region-params path (main's chain references it).
float smin2(float a, float b) {
	const float k = 10.0;
	return min(a, b) - (1.0 / k) * max(k - abs(a - b), 0.0);
}
`;
}

/** 2026-08-25 — the DEBUG-SHIM fragment shader (vscode integrated-browser perf escape hatch).
 *
 *  Per-fragment surface evaluator for `AssembleOptions.debugShim`.  Intentionally TINY +
 *  cheap-to-compile: instead of the real recipes' layer stacks / lighting / primitive
 *  evaluators / region params / variant ramps (the Set16/Set17 material shaders that can carry
 *  hundreds of uniforms + texel fetches — what freezes SwiftShader/ANGLE in vscode's integrated
 *  browser), every fragment:
 *
 *    1. resolves the recipe-set's SLOTS to deterministic hashed colours + strip angles — the
 *       hue comes from a golden-angle spiral of the recipe's packed seed (`u_debugShimColors`
 *       .a), and a per-material strip orientation from a distinct irrational step, so even
 *       neighbouring same-colour materials read as distinct bands;
 *    2. samples the chunk's REAL splat assignment field (`u_splatIndex`/`u_splatDist` at the
 *       fragment's lattice coord), and
 *    3. paints the WINNING material's colour, softly feathering toward the base (slot-0)
 *       colour by the slot-0 distance so region edges transition seam-free like the real splat.
 *
 *  It is fully SELF-CONTAINED (owns every uniform + helper it reads) so it can be emitted
 *  standalone.  The structural hash is FIXED (`debugShim`) → every recipe set maps to the SAME
 *  fragment string → a single module-cache entry → ONE WebGLProgram for the whole terrain — the
 *  entire point (no per-set/region compile during pan/zoom).  The renderer's per-chunk uniform
 *  setters still run (the uniforms exist on the template; three skips unused ones), so the
 *  geometry/hit-test plumbing is exercised unchanged.
 *
 *  `emitDebugShimGLSL` is emitted below with the region-clip / output-transfer blocks folded in
 *  when the matching assemble options are on (byte-matching the real path's behaviour).
 */
export const DEBUG_SHIM_MAX_MATERIALS = 8;
/** Fixed structural hash for the debug shim — every recipe set collapses to one cache entry. */
export const DEBUG_SHIM_STRUCTURAL_HASH = "debugShim";

/** Emit the self-contained debug-shim fragment (see AssembleOptions.debugShim). */
export function emitDebugShimGLSL(
	splatSize: number,
	terrainRegionClip: boolean,
	outputTransfer: boolean,
): string {
	return `#version 300 es
precision highp float;
#define CHUNK_N ${splatSize}  // splat lattice (must match the rasterised textures)
precision highp usampler2D;

in vec2 vUvLocal;                 // chunk-local [0,1] UV (three PlaneGeometry uv attribute)
uniform vec2  u_origin;           // chunk world-XZ bottom-left (set per chunk)
uniform float u_chunkSize;        // chunk world width (m) — maps world-XZ → lattice/strip scale
uniform float u_displaceScale;    // 0 in 2D (alpha clip), 1 in 3D (discard) — shared with the real path
uniform float u_opacity;          // 2D overlay opacity
uniform vec4  u_debugShimColors[${DEBUG_SHIM_MAX_MATERIALS}]; // per-slot: rgb = hashed colour (pre-pow), a = seed (hue + strip axis)

uniform usampler2D u_splatIndex;  // RGBA8UI: .r = the winning recipe index (per sample)
uniform sampler2D  u_splatDist;   // RGBA32F: per-slot signed distances, slots 0-3
uniform sampler2D  u_splatDist2;  // RGBA32F: per-slot signed distances, slots 4-7
${terrainRegionClip ? emitTerrainRegionClipGLSL() : ""}

layout(location = 0) out highp vec4 out_Color;

float shimHash01(float x) { return fract(sin(x * 127.1 + 311.7) * 43758.5453123); }
vec3 shimHSV(vec3 c) {
	vec3 p = abs(fract(c.xxx + vec3(0.0, 2.0 / 3.0, 1.0 / 3.0)) * 6.0 - 3.0);
	return c.z * mix(vec3(1.0), clamp(p - 1.0, 0.0, 1.0), c.y);
}
// The per-slot colour: prefer the packed hashed colour (u_debugShimColors), but when it is
// black/neutral (standalone tests assemble without packed values) decorate by slot+seed so
// distinct slots still resolve to distinct hues.
// 2026-08-26 — the hue/stripe come ONLY from the packed seed (p.a), NOT from the slot index:
// under the shim every chunk shares ONE program, but each chunk's splat indices index its OWN
// (per-chunk) recipe set — the renderer rewrites this chunk's u_debugShimColors so slot i
// carries the colour of the recipe that sits at slot i FOR THIS CHUNK. Keying the hue on the
// slot index would make the same recipe render shifted hues on different chunks; keying on the
// packed seed keeps the mapping recipe→colour stable across chunks.
vec3 shimSlotColor(int slot) {
	vec4 p = u_debugShimColors[slot % ${DEBUG_SHIM_MAX_MATERIALS}];
	if (p.r + p.g + p.b > 0.001) {
		float h = fract(p.a + p.a * 0.6180339887498949);
		vec3 hv = vec3(h, mix(0.6, 0.95, shimHash01(p.a)), mix(0.65, 0.95, shimHash01(p.a * 2.7)));
		return mix(p.rgb, shimHSV(hv), 0.35);
	}
	return shimHSV(vec3(fract(float(slot) * 0.6180339887498949), 0.75, 0.85));
}
float shimWeight(float d) { return 1.0 - smoothstep(-2.0, 2.0, d); }

void main() {
	vec2 wx = u_origin + vUvLocal * u_chunkSize;
	// NEAREST tie to the chunk's splat lattice (round — same mapping + seam-free at chunk
	// boundaries as the real splatSample).
	ivec2 lat = ivec2(round((wx - u_origin) / max(u_chunkSize, 1e-6) * float(CHUNK_N - 1)));
	uint idx = texelFetch(u_splatIndex, lat, 0).r;
	vec4 d0 = texture(u_splatDist, (vec2(lat) + 0.5) / float(CHUNK_N));
	vec4 d1 = texture(u_splatDist2, (vec2(lat) + 0.5) / float(CHUNK_N));
	int i = int(idx);
	vec3 col = shimSlotColor(i);
	// Feather the winning material into the base (slot 0) by the slot-0 distance so region edges
	// transition seam-free (mirrors the real splat's smoothstep feather).
	if (i != 0) col = mix(col, shimSlotColor(0), shimWeight(d0.r));
	// Strip decoration per MATERIAL (from its packed seed/slot) so same-colour neighbours read
	// as distinct bands; the band runs across the surface along the material's seed angle.
	// 2026-08-26 — derived from the seed only (see shimSlotColor note — the slot index is a
	// per-chunk concept; the seed is the material's stable identity).
	float ang = u_debugShimColors[i % ${DEBUG_SHIM_MAX_MATERIALS}].a;
	float dir = u_debugShimColors[i % ${DEBUG_SHIM_MAX_MATERIALS}].a * 6.283185307179586;
	float p = dot(vec2(cos(dir), sin(dir)), wx / max(u_chunkSize, 1e-3));
	float stripe = step(0.5, fract(p * 2.0 + ang));
	col = mix(col, col * 1.18, stripe * 0.55);
	out_Color = vec4(col, u_opacity);
	${terrainRegionClip ? `
	// 2026-08-13 terrain-region clip (matches the real path: 2D zeroes alpha, 3D discards).
	if (!terrainRegionClip(wx)) {
		if (u_displaceScale > 0.5) discard;
		else out_Color.a = 0.0;
	}` : ""}
	${outputTransfer ? `
	// Set14 P7 output transfer chain (tone-map + sRGB-encode into the renderer's output space).
	#ifdef TONE_MAPPING
	out_Color.rgb = toneMapping(out_Color.rgb);
	#endif
	out_Color = linearToOutputTexel(out_Color);` : ""}
}
`;
}

/** Assemble a fragment shader for the given resolved recipe set. Phase 2 = single-material (main evaluates recipes[0]); Phase 5's splat (`opts.useSplat`) replaces main with a ≤4-way evaluate-and-lerp. Phase 3 passes `includeLighting: true` so the shader carries the hillshade stage. */
export function assembleMaterialShader(recipes: ResolvedRecipe[], opts: AssembleOptions = {}): AssembleResult {
	if (recipes.length == 0) throw new MaterialAssembleError("assembleMaterialShader: empty recipe set");
	const includeLighting = opts.includeLighting === true;
	// Buildings Set1 §7.1 — SURFACE MODE: compile for an ARBITRARY MESH surface (a Building's
	// walls/floors/roof + Door/Window models) instead of a terrain chunk. Implies NO splat, NO
	// region params, NO terrain-region clip, and exactly ONE recipe. The sample coordinate +
	// geometry indirections come from per-vertex attributes (`aSurfaceUV`/`vSurfaceNormal`)
	// instead of the chunk uniforms/lattices (see the header + matGeoNormal/matGeoHeight seams).
	// All terrain branches below keep their literal strings, so the non-surface output is
	// byte-identical to pre-P6 (the P6 regression gate).
	const surfaceMode = opts.surfaceMode === true;
	const useSplat = opts.useSplat === true && !surfaceMode;
	const useRegionParams = opts.useRegionParams === true && useSplat;
	const useVariantParams = opts.useVariantParams === true && useSplat;
	const roughnessAwareSpecular = opts.roughnessAwareSpecular === true;
	const terrainRegionClip = opts.terrainRegionClip === true && !surfaceMode;
	const outputTransfer = opts.outputTransfer === true;
	const regionParamsColumns = useRegionParams ? (opts.regionParamsColumns ?? REGION_PARAMS_COLUMNS) : 0;
	if (useRegionParams && (!Number.isInteger(regionParamsColumns) || regionParamsColumns < 1)) throw new MaterialAssembleError(
		`assembleMaterialShader: regionParamsColumns must be an integer >= 1 (got ${regionParamsColumns})`);
	if (useSplat && (recipes.length < 1 || recipes.length > SPLAT_MAX_MATERIALS)) throw new MaterialAssembleError(
		`assembleMaterialShader: splat mode needs 1..${SPLAT_MAX_MATERIALS} recipes (got ${recipes.length}) — the shader has a fixed number of eval slots (plan §6: cap at ${SPLAT_MAX_MATERIALS})`);
	const splatSize = useSplat ? (opts.splatSize ?? SPLAT_LATTICE_SIZE) : 0;
	if (useSplat && (!Number.isInteger(splatSize) || splatSize < 2)) throw new MaterialAssembleError(
		`assembleMaterialShader: splatSize must be an integer >= 2 (got ${splatSize})`);

	// 2026-08-25 — DEBUG SHIM (vscode integrated-browser perf escape hatch).  When set, skip the
	// whole real assembly (layout collection, layer/prelude/eval emission, UBO/variant texture)
	// and emit ONLY the tiny self-contained shim — so the program's fragment STRING is constant
	// across every recipe set (fixed structural hash → single module-cache entry → ONE
	// WebGLProgram for the whole terrain).  Disabled when surfaceMode (buildings don't go through
	// the terrain splat).  The renderer's per-chunk setters still run (the uniforms exist on the
	// template; three skips unused ones), so geometry/hit-test plumbing is exercised unchanged.
	if (opts.debugShim === true && !surfaceMode) {
		const glsl = emitDebugShimGLSL(useSplat ? splatSize : SPLAT_LATTICE_SIZE, terrainRegionClip, outputTransfer);
		return {glsl, paramLayouts: [], structuralHash: DEBUG_SHIM_STRUCTURAL_HASH};
	}

	// Collect used params across the set, deduped by GLOBAL key `${recipeID}/${paramKey}` — each
	// recipe's params are scoped to its own layer args, so the same key in different recipes is a
	// DIFFERENT param (two user recipes extending the same base share param keys — Phase 7's core
	// use case — and must NOT collapse onto one uniform index; that would bind recipe B's refs to
	// recipe A's uniform values, and break the D10 free-variant set-hash too).
	//
	// Set9 P3 (2026-08-30) — the used-param ORDER is now CANONICAL (sorted by global key), routed
	// through `collectUsedParams` — the SAME canonical source the packer's `buildVariantParamLayouts`
	// uses, so the assembler's `variantParamColumns` (the GLSL `eval_i` texelFetch addresses) and
	// the packer's column layout are guaranteed in lock-step.  This is the parity-critical coupling
	// that was broken by F-split: a program shared by two same-`setHash` chunks with DIFFERENT
	// identity order (a recipe repeated by dominance-run splitting, e.g. `[starfield, dirt, GJ,
	// grass, GJ, water]`) must read the SAME canonical columns every chunk packs, or `eval_i` reads
	// the wrong recipe's params (Grass as Grass_Jungle, water-as-black).  Sorting here makes the
	// layout a pure function of the recipe SET (see the `collectUsedParams` docblock for the full
	// rationale).  NOTE this also reorders the UBO `u_p_<index>` slots + the variant columns →
	// a one-time GLSL recompile (expected + harmless).
	const collectedUsed = collectUsedParams(recipes);
	const usedParams = collectedUsed;
	// The canonical global-key order (what `variantParamColumns` + `paramLayouts` + per-recipe
	// lookups all index by).
	const usedParamKeys: string[] = collectedUsed.map(p=>p.key);
	// The raw-key map is not consumed by buildParamLookups today — retained as a no-op so the
	// call signature (and the D10 free-variant path) is unchanged.
	const usedParamGlobalKeys = new Map<string, string>();
	// Index-based uniform naming: u_p_<setIndex>.  Recipe ids never enter the GLSL, so identical
	// resolved structures → IDENTICAL GLSL → same cache key (the D10 free-variant property).
	const paramLayouts = computeParamLayouts(usedParams).map((l, i) => ({...l, index: i}));
	// Per-recipe raw-key → set-wide index maps (see buildParamLookups): each recipe's layer args
	// look up the set-wide uniform index through ITS OWN map, so two recipes sharing param keys
	// (both extending dirt) bind to distinct uniforms.
	const paramLookups = buildParamLookups(recipes, usedParamKeys, usedParamGlobalKeys);
	// Slot-keyed (Set9 P3): the caller passes the recipe's SLOT index, so an F-split run's two
	// instances of the same recipe resolve to their own blocks.
	const lookupForSlot = (ri: number): Map<string, number> => lookupFor(paramLookups, ri);

	// Phase 7 — regionModulated params get a fixed COLUMN in the per-region params texture (one
	// row per region; the column = the param's slot).  Only params whose schema declares
	// `regionModulated` participate; the texture has `regionParamsColumns` columns.  The map is
	// keyed by the RAW param key (per-recipe lookups resolve refs before consulting it).
	// Set12 P3 — the region row PREPENDS the fixed FEATHER block (REGION_FEATHER_BLOCK_COLUMNS
	// columns: feather, curve, noiseAmp, noiseScale, opaque — read by `setRegionIndex`), so the
	// regionModulated params start at column 5 (MUST match RegionParams'
	// `buildRegionParamLayoutsForFeather` — shared logic, tested against the assembler).
	const regionParamColumns = new Map<string, number>();
	if (useRegionParams) {
		let col = REGION_FEATHER_BLOCK_COLUMNS;
		for (const r of recipes) {
			for (const p of r.params) {
				if (p.regionModulated) {
					if (col >= regionParamsColumns) throw new MaterialAssembleError(
						`regionModulated params exceed the params-texture column budget (${regionParamsColumns})`);
					// GUARD (Set9 P3, 2026-08-30) — this map is keyed by the RAW param key, so two
					// recipes in one set that declare the same key (both extending dirt → both have
					// `dirt_scale`) SILENTLY overwrite each other's column, and an F-split run's two
					// slots of one recipe share a column.  Dormant today (no recipe sets
					// `regionModulated`), so fail LOUDLY rather than misrender when one does.
					// Fix direction: scope the key like the variant path's `globalParamKey`
					// (`${recipeID}/${slot}/${paramKey}`) and thread the recipe/slot into the
					// per-recipe lookup — but note region params vary per REGION (`g_regionIndex`,
					// the splat's per-sample payload) rather than per slot, so first decide whether
					// a column should be region-, slot-, or recipe-scoped; `RegionParams.ts`'s
					// `buildRegionParamLayoutsOffset` must change in lockstep (shared layout).
					if (regionParamColumns.has(p.key)) throw new MaterialAssembleError(
						`regionModulated param key "${p.key}" is declared by more than one recipe in this set `
						+ `(recipe "${r.id}") — the region-params column map is keyed by the RAW key, so the `
						+ `second declaration would overwrite the first and both recipes would read ONE column `
						+ `(silent wrong-values rendering).  See the Set9 P3 per-slot param-block fix for the `
						+ `precedent: scope the column key by recipe (and slot) and mirror it in RegionParams.ts.`);
					regionParamColumns.set(p.key, col);
					col += p.kind == "color" ? 3 : 1;
				}
			}
		}
	}

	// Set7 — the VARIANT params texture: EVERY used param (set-wide index order — the same
	// `usedParams` order the UBO layouts use) maps to its (texel, channel) in a variant row,
	// UNLESS it is `regionModulated` (that one wins — the per-region texture takes precedence).
	// The cursor mirrors `VariantParams.buildVariantParamLayouts` EXACTLY (same indexing:
	// scalar = 1 float, color = 3 floats texel-aligned, ramp = 6 texels; ramps + colours align
	// to texel boundaries — see VariantParams.ts).  A drift here breaks every variant row's
	// values — the `VariantParams.test.ts` parity test guards it.  Keyed by the set-wide INDEX
	// (u_p_<index> — the same the UBO uses), so argExprFor resolves a ref → index → slot.
	//
	// Row width: `opts.variantRowColumns` is an OPTIONAL explicit budget (bound-checked against
	// it).  When absent the width is AUTO-COMPUTED to fit the set (the plan §2.1 "width =
	// max(total columns) rounded up") — a set with many used params (e.g. starfield's ~97 used
	// uniforms) simply gets a wider row, and the manager's shared texture grows to match (the
	// fixed 256 default was too small for the live starfield set → the reported assemble throw).
	const variantParamColumns = new Map<number, {texel: number; channel: number; kind: ParamKind}>();
	const variantRowFixed = opts.variantRowColumns != null;
	let variantRowUsedWidth = 0; // the auto-computed end column (cursor after the last param)
	if (useVariantParams) {
		let col = 0;
		for (const [i, p] of usedParams.entries()) {
			if (p.regionModulated) continue; // region texture wins
			const span = p.kind == "ramp" ? RAMP_MAX_STOPS * 4 : (p.kind == "color" ? 3 : 1);
			if (p.kind == "ramp" || p.kind == "color") col = Math.ceil(col / 4) * 4;
			if (variantRowFixed && col + span > opts.variantRowColumns!) throw new MaterialAssembleError(
				`variant params exceed the params-texture column budget (${opts.variantRowColumns})`);
			variantParamColumns.set(i, {texel: Math.floor(col / 4), channel: col % 4, kind: p.kind});
			col += span;
		}
		variantRowUsedWidth = col;
	}
	// The width the GLSL's comment advertises: the explicit budget if given, else the auto
	// width floored at VARIANT_PARAMS_COLUMNS + rounded up to a texel boundary (the texel count
	// is only cosmetic — texelFetch addresses are absolute per-param, so a wider texture than
	// the shader's declared width is harmless).
	const variantRowEmitWidth = variantRowFixed
		? opts.variantRowColumns!
		: Math.max(VARIANT_PARAMS_COLUMNS, Math.ceil(variantRowUsedWidth / 4) * 4);

	const parts: string[] = [];
	// CHUNK_N is the shared lattice size for BOTH the splat field (Phase 5) and the per-sample
	// geometric-normal fetch (u_geoNormal — the hillshade/mask geo input).  Must match the
	// uploaded textures (the worker's 33×33 sample lattice; plan §12.1).  Emitted whenever
	// either consumer is present (matGeoNormal() texelFetches it even in the non-splat path).
	// Surface mode needs no lattice (the geometry normal is a per-vertex attribute), so the
	// define is omitted there.
	const needsLatticeDefine = !surfaceMode && (useSplat || includeLighting);
	const latticeSize = useSplat ? splatSize : SPLAT_LATTICE_SIZE;
	// Surface-mode per-vertex inputs: `vSurfaceUV` (metres along the surface — the sample
	// coordinate, replacing world-XZ `wx`), `vSurfaceNormal` (the interpolated GEOMETRY normal,
	// replacing the u_geoNormal lattice — the lighting-stage base normal), and the
	// `u_surfaceElevation` uniform (the mesh's world Z origin, so a recipe referencing the
	// `geoHeight` primitive still compiles + reads a sane elevation).
	const surfaceVaryings = surfaceMode
		? `in vec2 vSurfaceUV;             // surface UV in metres (per-vertex; the surface-mode sample coordinate)
in vec3 vSurfaceNormal;        // interpolated geometry normal (surface-mode geo normal for lighting/mask)
uniform float u_surfaceElevation;  // the mesh's world Z origin — the surface-mode matGeoHeight() value`
		: "";
	parts.push(`#version 300 es
precision highp float;
${needsLatticeDefine ? `#define CHUNK_N ${latticeSize}  // lattice size for the splat + per-sample geometric normal (u_geoNormal) — must match the uploaded textures (plan §12.1)` : ""}

in vec2 vUvLocal;             // chunk-local [0,1] UV (three PlaneGeometry uv attribute, interpolated)
in vec3 vWorldPos;            // Set15 P3 (D10) — world position of the displaced vertex (the real-view-vector specular input)
uniform vec2  u_origin;          // the chunk's world-XZ bottom-left (set per chunk via setChunkOrigin)
uniform float u_metersPerPixel;  // zoom level — constant across the screen under top-down ortho (D5; AA only, NOT position). In surface mode: the surface-UV metres-per-pixel (texture detail scale).
uniform float u_displaceScale;   // Set15 P3 (D4) — 0 in 2D (byte-identical), 1 in 3D. Gates matPixelSize fwidth (D9), the region-clip discard (D5), and the real view vector (D10). Surface mode: 1 (buildings are 3D meshes).
uniform vec3  u_baseAlbedo;      // hypsometric-ramp colour, demoted to base albedo input (plan §8); the renderer computes it per chunk. Surface mode: unused (buildings carry their own albedo in the recipe) but kept for the shared eval signature.
uniform float u_chunkSize;       // the chunk's world width (m) — maps world XZ → lattice coords (splat + u_geoNormal + u_geoHeight). Unused in surface mode.
uniform sampler2D u_geoHeight;   // 2026-08-13 — per-chunk height lattice (R32F, worker's heights). The geoHeight primitive samples it for elevation-reactive colour (water depth). MUST be set per chunk by the renderer when any recipe uses geoHeight.
${surfaceVaryings}
${(includeLighting || surfaceMode) ? LIGHTING_UNIFORM_DECLS : ""}
${AMBIENT_DETAIL_UNIFORM_DECLS}
${useSplat ? emitSplatGLSL(useRegionParams) : ""}
${terrainRegionClip ? emitTerrainRegionClipGLSL() : ""}
${roughnessAwareSpecular ? `uniform float u_varianceGain;    // Set11 Phase 2 — Toksvig widening gain (default 1.0; 0.0 = exact no-op)` : ""}
${(useVariantParams) ? "" : emitUBO(paramLayouts)}
${useVariantParams ? emitVariantParamsGLSL(variantRowEmitWidth) : ""}

layout(location = 0) out highp vec4 out_Color;

${MATERIAL_SAMPLE_GLSL}

// matPixelSize MUST be declared before the primitive prelude (fbmVal/scatterSample call it).
// Set15 P3 (D9): under 2D (u_displaceScale 0) this is the constant u_metersPerPixel
// (byte-identical to the pre-3D seam).  Under 3D (1) the seam returns the world-space PIXEL
// FOOTPRINT via fwidth of the chunk-local UV (times u_chunkSize -> world metres per pixel) —
// screen-locked-lattice semantics preserved, but now correct under perspective where the
// per-pixel world size varies with distance.  The clamp is mandatory: at grazing angles
// fwidth explodes and would over-blur the finite-difference / AA steps.
// Surface mode: the surface UV IS in metres, so matPixelSize is the constant u_metersPerPixel
// (no fwidth against a plane's [0,1] UV, which would be zoom-dependent but not surface-scaled).
float matPixelSize() {
	${surfaceMode ? "return u_metersPerPixel;" : "return mix(u_metersPerPixel, clamp(max(fwidth(vUvLocal.x), fwidth(vUvLocal.y)) * u_chunkSize, 1e-4, 128.0), u_displaceScale);"}
}

// Geometry/light INDIRECTIONS (same seam idea as matPixelSize, D5): the mask primitive and the
// lightModulatedRamp blend need the geometric normal + light direction, but those uniforms only
// exist when lighting is enabled — and a recipe using them must still assemble in the Phase-2
// unlit path.  Unlit falls back to straight-up / a fixed overhead-ish light, so an unlit
// assemble is valid GLSL with sane (if unshaded) values.
//
// matGeoNormal() fetches the chunk's PER-SAMPLE geometric normal lattice (u_geoNormal, RGBA32F
// — the worker's normals array, slope_scale ALREADY applied) at the fragment's lattice coord,
// the same wx → lat mapping splatSample uses.  This is what gives hillshade + the mask
// primitive's slope coverage fine-cell granularity (matching the old CPU per-texel bake); the
// Phase-3 per-chunk centre-sample uniform collapsed both to one slope per chunk.
//
// NEAREST-SAMPLE (round, not floor) is REQUIRED for chunk-boundary continuity: the worker's
// lattice texel i sits at world x = u_origin.x + (i/(CHUNK_N-1)) * u_chunkSize, so texel
// (CHUNK_N-1) sits EXACTLY on the chunk's far edge (world x = u_origin.x + u_chunkSize).
// A fragment just inside that edge has (wx-u_origin)/u_chunkSize * (CHUNK_N-1) = 31.999…;
// floor() → texel 31 (one cell short) while the NEIGHBOUR chunk's near-edge fragment maps to
// its texel 0 (the same world sample) — a one-sample hillshade jump at the shared boundary
// (a dark seam that shifts with zoom).  round() maps the boundary fragment to texel 32 = the
// neighbour's texel 0, so both chunks read the SAME normal across the boundary.
${surfaceMode
		? `vec3 matGeoNormal() { return normalize(vSurfaceNormal); }
float matGeoHeight() { return u_surfaceElevation; }
vec3 matLightDir() { return u_lightDir; }
float matAmbientSlopeScale() { return 1.0; }`
		: (includeLighting
			? `vec3 matGeoNormal() {
  vec2 wx = u_origin + vUvLocal * u_chunkSize;
  ivec2 lat = ivec2(round((wx - u_origin) / max(u_chunkSize, 1e-6) * float(CHUNK_N - 1)));
  return texelFetch(u_geoNormal, lat, 0).rgb;
}
// 2026-08-13 — matGeoHeight(): the per-sample HEIGHT lattice (u_geoHeight, R32F) at the
// fragment's lattice coord — the SAME wx→lat mapping matGeoNormal uses (round, not floor —
// shared lattice → seam-free at chunk boundaries).  The geoHeight primitive maps this to an
// elevation-reactive colour (water depth/current).  The unlit branch returns a constant 0.0 so
// the primitive compiles in the unlit path too (the matGeoNormal precedent).
float matGeoHeight() {
  vec2 wx = u_origin + vUvLocal * u_chunkSize;
  ivec2 lat = ivec2(round((wx - u_origin) / max(u_chunkSize, 1e-6) * float(CHUNK_N - 1)));
  return texelFetch(u_geoHeight, lat, 0).r;
}
vec3 matLightDir() { return u_lightDir; }
float matAmbientSlopeScale() { return u_slopeScale; }`
			: `vec3 matGeoNormal() { return vec3(0.0, 0.0, 1.0); }
float matGeoHeight() { return 0.0; }
vec3 matLightDir() { return normalize(vec3(0.4, 0.4, 0.82)); }
// Set10 — the ambient detail normal follows the geometric normal's slope-scale exaggeration
// (§5.3 decision: apply u_slopeScale to the shader band too, so the crossover has no amplitude
// discontinuity).  Unlit assemblies have no u_slopeScale — fall back to 1.0 (the same seam
// idea as matGeoNormal/matLightDir: a valid-GLSL, sane-value indirection).
float matAmbientSlopeScale() { return 1.0; }`)}

${PRIMITIVE_PRELUDE}
${(includeLighting || surfaceMode) ? LIGHTING_GLSL : ""}
${useSplat ? emitSplatFeatherGLSL(useRegionParams) : ""}`);

	recipes.forEach((r, ri) => {
		r.layers.forEach((l, li) => parts.push(emitLayerFn(ri, li, r, l, lookupForSlot(ri), useRegionParams, regionParamColumns, useVariantParams, variantParamColumns)));
	});
	recipes.forEach((r, ri) => {
		// Set11 Phase 1 — the analytic-derivative wrappers + the gradient chain (dChain).  The layers' D functions must precede dChain (declare-before-use).
		r.layers.forEach((l, li) => parts.push(emitLayerDerivFn(ri, li, r, l, lookupForSlot(ri), useRegionParams, regionParamColumns, useVariantParams, variantParamColumns)));
		parts.push(emitHChain(ri, r));
		parts.push(emitDChain(ri, r));
		// Set11 Phase 2 — the per-layer variance wrappers + the variance chain (only when the roughness-aware specular path is on; off = absent, byte-identical to pre-Phase-2).
		if (roughnessAwareSpecular) {
			r.layers.forEach((l, li) => parts.push(emitLayerVarianceFn(ri, li, r, l, lookupForSlot(ri), useRegionParams, regionParamColumns, useVariantParams, variantParamColumns)));
			parts.push(emitVarChain(ri, r));
		}
		parts.push(emitEval(ri, r, lookupForSlot(ri), useRegionParams, regionParamColumns, useVariantParams, variantParamColumns, {roughnessAwareSpecular}));
	});

	// Phase 3: base albedo in, lit colour out (per-sample geo normal matGeoNormal() + detail
	// normal blended inside lightingShade).  Phase 5 (useSplat): main evaluates the ≤4 recipes
	// the sample's splat texel references and lerps by weight (plan §6). The fallback (slot 0)
	// always evaluates — a -1 index must still produce a colour; the packer renormalises weights
	// to sum 1 so the -1 case is w0 = 1, rest 0.
	let mainBody: string;
	// Set14 P9 — the PBR lighting call ALWAYS passes s.roughness through (the old 3-arg hardcoded-32
	// Blinn-Phong path was deleted).  The per-recipe specStrength (0 = that material opts out of
	// specular) is baked as a literal 5th arg, so it must be resolved per RECIPE INDEX — under
	// useSplat several recipes share one main().
	// flatGeoNormal (per-recipe): substitute a straight-up geo normal for the u_geoNormal lattice, so
	// only the material's own (world-XZ, therefore camera-invariant) detail normal shades it.
	const geoN = (ri: number): string => (recipes[ri].flatGeoNormal ?? false) ? `vec3(0.0, 0.0, 1.0)` : `matGeoNormal()`;
	// lightAltitudeOverride (per-recipe): baked as the 6th arg; -1 means "no override" (the shader gates
	// on `>= 0`), and the 5-arg call is emitted unchanged when absent so existing GLSL is byte-identical.
	const litCall = (expr: string, ri: number): string => {
		const args = `${expr}.albedo, ${geoN(ri)}, ${expr}.normal, ${expr}.roughness, ${floatLit(recipes[ri].specStrength ?? 1)}`;
		const altOverride = recipes[ri].lightAltitudeOverride ?? null;
		if (altOverride == null) return `lightingShade(${args})`;
		return `lightingShade(${args}, ${floatLit(altOverride)})`;
	};
	if (useSplat) {
		const lit = (ri: number): string => `eval_${ri}(wx, u_baseAlbedo)`;
		// Detail-normals blend through the same whiteout path (lightingShade) — each eval carries its own normal; lerping albedo alone would flatten the relief.  Set11 Phase 2: under roughnessAwareSpecular, roughness lerps with the SAME splat weight as albedo (plan §3.3).
		const acc = includeLighting
			? (ri: number): string => litCall(lit(ri), ri)
			: (ri: number): string => `${lit(ri)}.albedo`;
		const stmts: string[] = [
			`  vec2 wx = u_origin + vUvLocal * u_chunkSize;`,
			`  ivec2 lat; uvec4 splatIdx; vec4 splatD; vec4 splatD2;`,
			`  splatSample(wx, lat, splatIdx, splatD, splatD2);`,
			...(useRegionParams ? [`  setRegionIndex(lat);`] : []),
			`  vec3 col = ${acc(0)};`,
		];		// The DISTANCE component must match the material's SLOT (d0 = slot 0 = the fallback,
		// never blended here) → .g for 1, .b for 2, .a for 3. (Off-by-one silently renders
		// the material at weight 0.)  P3: the blend factor is the shader-side `regionWeight`
		// (plan §3.6) over the slot's signed distance — NOT the raw texel value (which is a
		// distance in metres, negative inside).  There is NO index gate — every slot blends
		// everywhere with its OWN weight (absent slots are +huge → weight 0 by the rasteriser,
		// so the Phase-5 gate's job is done by the distance field): the winner's colour lerps
		// from the under-layer at the boundary ("where two materials meet they blend", plan
		// §1.3) and is full inside (its clamped −d → weight 1).  OPAQUE regions need no shader
		// special-casing either: the rasteriser cleared the strictly-lower-dominance slots to
		// +huge inside an opaque winner, and regionWeight(+huge) = 0 for ANY feather → the
		// underlying layers contribute nothing (plan §7 GPU test 2), while the winner's own
		// weight is 1 → col lands exactly on the opaque material.
		// Chain ORDER: slots blend ASCENDING dominance (slot K → … → slot 1 LAST = on top,
		// plan §4.2 A).  The base (slot 0, lowest) is the chain's start colour; the highest-
		// dominance region is mixed last, so at a 3-way junction the top material shows where
		// it is present and the next-highest shows where it isn't.  (Slot i holds the
		// i-th-highest dominance — the rasteriser assigns slots in the dominance-sorted region
		// order, so the chain iterates HIGH slot index → LOW.)
		// P5 — the equal-dominance smin soft-union (plan §4.2 A→C): the chain is emitted as
		// ADJACENT SLOT PAIRS (i, i-1) walking HIGH → LOW; each pair's runtime dominance gate
		// (u_slotDominance.<i> == u_slotDominance.<i-1> — equal-dominance regions are always
		// CONSECUTIVE slots, since slots are assigned in dominance-sorted order) selects
		// between the C soft-union (smin the two DISTANCE fields → the pair's composite
		// coverage + a relative colour blend) and the A ordered paint-over (the two P3 mixes).
		// The smin is on the DISTANCE FIELD (metres), not the weight — the plan's §4.2 C
		// wording — so the pair's boundary is the SOFT UNION of the two feather curves (the
		// "two equal-dominance strokes meeting" case has NO hard seam), while each member's
		// own interior (weight 1) still renders at full strength within the relative blend.
		// A same-dominance slot that is ABSENT at the sample (+huge) contributes w=0 and its
		// smin distance collapses to the present member's (min picks it) — the pair reduces
		// to the present member's own mix, so an odd-length chain (a trailing unpaired slot 1)
		// is the plain P3 single mix.
		// Set7 §9 — the 8-way raise: slots 0-3 live in `splatD` + `u_slotDominanceA`, slots 4-7
		// in `splatD2` + `u_slotDominanceB`.  `lane(slot)` maps a slot to its per-half component.
		const COMPONENT = ["r", "g", "b", "a"];
		// `dVec`/`domVec` pick the distance vec4 + dominance vec4 for a slot (A for 0-3, B for 4-7).
		const dVec = (s: number): string => (s < 4 ? "splatD" : "splatD2");
		const domVec = (s: number): string => (s < 4 ? "u_slotDominanceA" : "u_slotDominanceB");
		// Set12 followup — the per-slot resolved FEATHER vec4 (A for slots 0-3, B for slots 4-7);
		// `regionWeight` feathers each slot with its OWN feather (see emitSplatFeatherGLSL).
		const fVec = (s: number): string => (s < 4 ? "u_slotFeatherA" : "u_slotFeatherB");
		// Set12 P5 — the per-slot EDGE-NOISE vec4s (A for slots 0-3, B for slots 4-7): each slot
		// displaces its distance with ITS OWN noise (amp/scale), so the warp applies on BOTH
		// sides of the polygon edge (see emitSplatFeatherGLSL's regionWeight docblock).
		const ampVec = (s: number): string => (s < 4 ? "u_slotNoiseAmpA" : "u_slotNoiseAmpB");
		const scaleVec = (s: number): string => (s < 4 ? "u_slotNoiseScaleA" : "u_slotNoiseScaleB");
		// Set12 followup — the per-slot SOFT-UNION opt-in vec4 (A for slots 0-3, B for slots 4-7):
		// the smin branch only fires for an equal-dominance pair when BOTH slots are participants
		// (see the u_slotSoftUnion uniform comment).  A far-but-bbox-overlapping equal-dominance
		// region in the adjacent slot can therefore never be accidentally soft-unioned with it.
		const suVec = (s: number): string => (s < 4 ? "u_slotSoftUnionA" : "u_slotSoftUnionB");
		const lane = (s: number): string => COMPONENT[s % 4];
		// Adjacent pairs (i, i-1), i = len-1, len-3, … ≥ 2 — each pair consumes two slots.
		for (let i = recipes.length - 1; i >= 2; i -= 2) {
			const dI = dVec(i), dIM = dVec(i - 1);
			const cI = lane(i), cIM = lane(i - 1);
			const domI = domVec(i), domIM = domVec(i - 1);
			const fI = fVec(i), fIM = fVec(i - 1);
			const aI = ampVec(i), aIM = ampVec(i - 1);
			const sI = scaleVec(i), sIM = scaleVec(i - 1);
			const suI = suVec(i), suIM = suVec(i - 1);
			stmts.push(`  {
    float wA = regionWeight(wx, ${dI}.${cI}, ${fI}.${cI}, ${aI}.${cI}, ${sI}.${cI});
    float wB = regionWeight(wx, ${dIM}.${cIM}, ${fIM}.${cIM}, ${aIM}.${cIM}, ${sIM}.${cIM});
    if (${domI}.${cI} == ${domIM}.${cIM} && ${suI}.${cI} > 0.5 && ${suIM}.${cIM} > 0.5) {
      // P5 equal-dominance pair (BOTH slots soft-union opt-in): smin the distance fields (plan
      // §4.2 C) — the composite coverage is the soft union; the colours share the point by their
      // relative weights.  The smin'd composite is feathered with the SOFTER of the pair's own
      // feathers (max) so both members' clamped distances saturate correctly at their own band.
      float wAB = regionWeight(wx, smin2(${dI}.${cI}, ${dIM}.${cIM}), max(${fI}.${cI}, ${fIM}.${cIM}), max(${aI}.${cI}, ${aIM}.${cIM}), ${sI}.${cI});
      float wSum = wA + wB;
      col = mix(col, mix(${acc(i)}, ${acc(i - 1)}, wB / max(wSum, 1e-6)), wAB);
    } else {
      // Equal dominance without BOTH soft-union opt-ins (or distinct dominance) → the ordered
      // paint-over: each slot feathers with its OWN weight, so a far-but-bbox-overlapping
      // region's positive distance → weight 0 (no phantom bleed — the "water replaces base"
      // footgun fix).
      col = mix(col, ${acc(i)}, wA);
      col = mix(col, ${acc(i - 1)}, wB);
    }
  }`);
		}
		// An EVEN-length chain leaves slot 1 (the highest-dominance slot) unpaired — its single
		// P3 mix (an odd-length chain's pairs already consumed slot 1).
		if (recipes.length % 2 == 0) {
			stmts.push(`  col = mix(col, ${acc(1)}, regionWeight(wx, splatD.g, u_slotFeatherA.g, u_slotNoiseAmpA.g, u_slotNoiseScaleA.g));`);
		}
		// Weight sum is 1 by construction (the packer renormalises); a defensive renormalise would reorder the lerp.
		mainBody = stmts.join("\n") + (includeLighting
			? "\n  out_Color = vec4(col, u_opacity);"
			: "\n  out_Color = vec4(col, 1.0);");
	} else {
		mainBody = includeLighting || surfaceMode
			? `
  vec2 wx = ${surfaceMode ? "vSurfaceUV" : "u_origin + vUvLocal * u_chunkSize"};
  MaterialSample s = eval_0(wx, u_baseAlbedo);
  out_Color = vec4(${litCall("s", 0)}, u_opacity);`
			: `
  vec2 wx = u_origin + vUvLocal * u_chunkSize;
  MaterialSample s = eval_0(wx, u_baseAlbedo);
  out_Color = vec4(s.albedo, 1.0);`;
	}
	// 2026-08-13 — terrain-region clip: zero the alpha outside every active TerrainRegion AABB
	// (matches the legacy bake path's per-pixel alpha test).  Runs after out_Color is written.
	// Set15 P3 (D5) — under 3D the terrain is OPAQUE (transparent:false, depthWrite:true), so
	// zeroing alpha does nothing and an invisible fragment would still occlude via depth.  When
	// `u_displaceScale` is 1 (3D) the clip becomes `discard`, which must run BEFORE the depth
	// write (R8) so coarse region-boundary chunks can't occlude with invisible fragments.  In
	// 2D (u_displaceScale 0) the alpha zero-out is kept byte-identical.
	if (terrainRegionClip) mainBody += `
  if (!terrainRegionClip(wx)) {
    if (u_displaceScale > 0.5) discard; // 3D opaque — discard before depth write (D5/R8)
    else out_Color.a = 0.0;             // 2D transparent — zero alpha (byte-identical)
  }`;
	// Set14 P7 §4.11 — the OUTPUT TRANSFER CHAIN (runs AFTER the region clip, so the clipped
	// alpha survives): apply three's LIVE tone-mapping + sRGB encode to out_Color. three injects
	// `toneMapping()` (only when the renderer's toneMapping != NoToneMapping — hence the
	// `#ifdef TONE_MAPPING` guard) and `linearToOutputTexel()` (unconditionally) into the prefix
	// per the RENDERER's CURRENT settings, so calling them directly keeps the setting fully live
	// without hard-coding ACES. `linearToOutputTexel` touches `.rgb` only — the region-clip/
	// opacity alpha in `.a` is preserved. Under `NoToneMapping` + a LINEAR output colour space
	// (where `linearToOutputTexel` is the identity) this block is byte-identical to the pre-P7
	// output. This closes the tree↔terrain brightness/contrast gap opened in P4 (trees, via
	// `MeshStandardMaterial`'s built-in includes, are already on this chain; the terrain was raw).
	if (outputTransfer) mainBody += `
  // Set14 P7 §4.11 — output transfer chain (tone-map + sRGB-encode into the renderer's output space).
  #ifdef TONE_MAPPING
  out_Color.rgb = toneMapping(out_Color.rgb);
  #endif
  out_Color = linearToOutputTexel(out_Color);`;
	parts.push(`
void main() {${mainBody}
}`);

	return {
		glsl: parts.join("\n"),
		paramLayouts,
		structuralHash: materialSetHash(recipes),
	};
}

/** Program cache keyed by the structural hash of the active material set (D1).  Exported (not
 *  private) so Phase 6's warm-up can pre-compile sets into the SAME cache the live path reads —
 *  a warm program is a cache HIT at first appearance, so the first frame after a set change pays
 *  no compile.  This is the FRAGMENT-layer cache (compileRawFragmentProgram); the three.js layer
 *  is pre-warmed separately by TerrainMaterialManager (scratch ShaderMaterials — three's own
 *  cacheKey is pre-warmable by compiling the identical fragment string through the same renderer). */
export const programCache = new Map<string, WebGLProgram>();
export function __testOnly_programCacheSize(): number { return programCache.size; }
export function __testOnly_clearProgramCache(): void { programCache.clear(); }
/** Test-only snapshot of the cache KEYS (set hashes) — lets tests assert exactly which sets were
 *  pre-warmed (Phase 6: prefix enumeration + params-only dedupe) without a GL context. */
export function __testOnly_programCacheKeys(): string[] { return [...programCache.keys()]; }

/** Compile (or fetch) the program for the given resolved recipe set. Cache key = materialSetHash
 *  (set-level).  Phase 6's warm-up pre-populates this cache via {@link compileMaterialProgram} so a
 *  later first-appearance of the same set is a HIT (no compile hitch). */
export async function compileMaterialProgram(
	gl: WebGL2RenderingContext,
	recipes: ResolvedRecipe[],
	compileLink: (gl: WebGL2RenderingContext, fragmentSrc: string) => WebGLProgram,
): Promise<{program: WebGLProgram; result: AssembleResult}> {
	const result = assembleMaterialShader(recipes);
	const cached = programCache.get(result.structuralHash);
	if (cached) return {program: cached, result};
	const program = compileLink(gl, result.glsl);
	programCache.set(result.structuralHash, program);
	return {program, result};
}
